import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import api, {
  userAPI,
  masterDataAPI,
  categoryMasterAPI,
  territoryMasterAPI,
  regionMasterAPI,
  zoneMasterAPI,
  plantMasterAPI,
  rdcMasterAPI,
  API_UPLOAD_TIMEOUT_MS,
} from "../services/api";
import Swal from "sweetalert2";
import { showSwal } from "../utils/swalHelper";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import DataTable from "datatables.net-bs5";
import $ from "jquery";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import "./RaiseComplaint.css";
import "./UserManagement.css";
import "./AddEditUser.css";
import { getRoutePermissions } from "../utils/auth";
import {
  isUserGeoDefaultCode,
  isGeneralUserSpec,
  pickUserGeoCodesFromApi,
  resolveUserGeoCodesForSave,
} from "../utils/userTerritoryScope";
import {
  applyLocationMappingsToForm,
  buildLocationMappingsFromSelections,
} from "../utils/userLocationMapping";
import {
  normalizeExcelHeader,
  findBulkExcelHeaderRow,
  findMasterExcelHeaderRow,
  getExcelCellText,
  USER_MANAGEMENT_EXCEL_HEADERS,
  USER_BULK_UPLOAD_REQUIRED_HEADERS,
  MASTER_USER_EXCEL_HEADERS,
  MASTER_BULK_UPLOAD_REQUIRED_HEADERS,
  validateBulkUserRows,
  validateUserCategoryAndGeo,
  resolveBusinessVerticalCodes,
  resolveUserTypeFromMaster,
  findRoleScopeConflict,
  buildRoleScopeSlots,
  bvKeysFromCodes,
  slotsFromListedUser,
  buildBulkValidationErrorExcel,
  buildBulkUploadResultExcel,
} from "../utils/userCategoryGeoValidation";
import CustomMultiSelect from "../components/common/CustomMultiSelect";
import FileUploadPanelLayout from "../components/FileUploadPanelLayout";
import ExcelJS from "exceljs";
import {
  mapExportZonesToOptions,
  resolveZoneCodeForCountry,
  findZoneOption,
} from "../utils/dashboardGeoFilterUtils";

/** Normalize API envelope (camelCase or PascalCase). */
const parseApiEnvelope = (response) => {
  if (!response) return { ok: false, payload: null };
  const ok = response.success === true || response.Success === true;
  const payload = response.data ?? response.Data ?? null;
  return { ok, payload };
};

const getMarketCode = (m) =>
  String(m?.marketCode || m?.MARKETCODE || m?.MarketCode || "").trim();

const getMarketName = (m) =>
  String(m?.marketName || m?.MARKETNAME || m?.MarketName || "").trim();

const NA_DISPLAY = "N/A";

const isBlankOrNa = (value) => {
  const text = String(value ?? "").trim();
  return !text || text === "-" || text.toUpperCase() === "N/A";
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const formatGeoTooltipHtml = (value) => {
  const text = String(value ?? "").trim();
  if (!text || text === "-") return escapeHtml(text || "-");
  const parts = text
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length <= 1) return escapeHtml(text);
  return parts.map((part) => escapeHtml(part)).join("<br>");
};

const renderGeoTooltipCell = (displayValue, type) => {
  const text =
    displayValue == null || displayValue === "" ? "-" : String(displayValue);
  if (type !== "display") return text;
  const display = escapeHtml(text);
  const tooltipHtml = formatGeoTooltipHtml(text);
  return `<span class="user-geo-tooltip-cell user-geo-tip"
    data-bs-toggle="tooltip"
    data-bs-placement="top"
    data-bs-html="true"
    data-bs-title="${tooltipHtml}"
    title="${display}">${display}</span>`;
};

const initUserGeoTooltips = (tableEl) => {
  if (!tableEl || typeof window === "undefined" || !window.bootstrap?.Tooltip)
    return;
  tableEl.querySelectorAll('[data-bs-toggle="tooltip"].user-geo-tip').forEach((el) => {
    const existing = window.bootstrap.Tooltip.getInstance(el);
    if (existing) existing.dispose();
    new window.bootstrap.Tooltip(el, {
      html: true,
      placement: "top",
      container: "body",
      customClass: "user-geo-cell-tooltip",
      trigger: "hover focus",
    });
  });
};

const disposeUserGeoTooltips = (tableEl) => {
  if (!tableEl || typeof window === "undefined" || !window.bootstrap?.Tooltip)
    return;
  tableEl.querySelectorAll('[data-bs-toggle="tooltip"].user-geo-tip').forEach((el) => {
    const existing = window.bootstrap.Tooltip.getInstance(el);
    if (existing) existing.dispose();
  });
};

const formatBulkUserType = (row) => {
  const spec = String(
    row?.userSpecificationType ?? row?.UserSpecificationType ?? ""
  ).trim();
  return isBlankOrNa(spec) ? NA_DISPLAY : spec;
};

/** User Type: General / Plant / RDC / BCM / CPD. Role is a separate field (userTypeCode). */
const normalizeUserSpecificationType = (raw) => {
  const parsed = parseUserSpecificationType(raw);
  return parsed || "General";
};

const parseUserSpecificationType = (raw) => {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  if (/^plant$/i.test(s)) return "Plant";
  if (/^rdc$/i.test(s)) return "RDC";
  if (/^bcm$/i.test(s)) return "BCM";
  if (/^cpd$/i.test(s) || /^loghead$/i.test(s) || /^log.?head$/i.test(s)) return "CPD";
  if (/^general$/i.test(s)) return "General";
  return "";
};

const inferMasterUserType = ({ plant, role, explicit } = {}) => {
  const fromCol = parseUserSpecificationType(explicit);
  if (fromCol) return fromCol;
  if (String(plant || "").trim()) return "Plant";
  const r = String(role || "").trim().toUpperCase();
  if (
    r === "CPD" ||
    r.includes("CPD HEAD") ||
    r.includes("LOGISTIC HEAD") ||
    r.includes("HO LOGISTIC")
  ) {
    return "CPD";
  }
  if (r === "BCM" || r.includes("BCM HEAD") || r.startsWith("BCM ")) {
    return "BCM";
  }
  return "General";
};

const isPlantOrRdcSpec = (raw) => {
  const s = normalizeUserSpecificationType(raw);
  return s === "Plant" || s === "RDC";
};

const formatBulkPlantOrRdc = (row) => {
  const spec = String(
    row?.userSpecificationType ?? row?.UserSpecificationType ?? ""
  ).trim();
  const plant = String(row?.plantIds ?? row?.PlantIds ?? "").trim();
  const rdc = String(row?.rdcIds ?? row?.RdcIds ?? "").trim();
  if (/^plant$/i.test(spec)) return isBlankOrNa(plant) ? NA_DISPLAY : plant;
  if (/^rdc$/i.test(spec)) return isBlankOrNa(rdc || plant) ? NA_DISPLAY : rdc || plant;
  if (!isBlankOrNa(plant)) return plant;
  if (!isBlankOrNa(rdc)) return rdc;
  return NA_DISPLAY;
};

const pickFirstDropdownText = (list, nameKeys, skipRow) => {
  const rows = Array.isArray(list) ? list : [];
  for (const row of rows) {
    if (skipRow && skipRow(row)) continue;
    for (const key of nameKeys) {
      const v = String(row?.[key] ?? "").trim();
      if (v) return v;
    }
  }
  return "";
};

const isAdminUserTypeRow = (ut) => {
  const code = String(ut?.userTypeCode || ut?.UserTypeCode || "")
    .trim()
    .toUpperCase();
  const name = String(ut?.userTypeName || ut?.UserTypeName || "")
    .trim()
    .toUpperCase();
  return code === "UT0000017" || name === "ADMIN ROLE" || name === "ADMIN";
};

/** Three sample rows only: General, Plant, RDC. */
const buildBulkUploadSampleRows = (dropdowns) => {
  const dd = dropdowns || {};
  const role =
    pickFirstDropdownText(
      dd.userTypes,
      ["userTypeName", "UserTypeName"],
      isAdminUserTypeRow
    ) || "Initiator";
  const market =
    pickFirstDropdownText(dd.markets, ["marketName", "MARKETNAME", "MarketName"]) ||
    "Domestic";
  const country =
    pickFirstDropdownText(dd.countries, [
      "countryName",
      "CountryName",
      "COUNTRYNAME",
    ]) || "ALL";
  const bv =
    pickFirstDropdownText(dd.businessVerticals, [
      "catgName",
      "CatgName",
      "CATGNAME",
      "bvName",
      "BVName",
    ]) || "";
  const plant =
    pickFirstDropdownText(dd.plants, ["plantName", "PlantName", "PLANTNAME"]) ||
    "Plant Name";
  const rdc =
    pickFirstDropdownText(dd.rdcs, ["rdcName", "RdcName", "RDCNAME"]) ||
    "RDC Name";

  return [
    {
      employeeCode: "EMP001",
      adId: "sample.general",
      fullName: "Sample General User",
      email: "sample.general@example.com",
      mobileNumber: "",
      role,
      market,
      country,
      zone: "ALL",
      region: "ALL",
      territory: "ALL",
      businessVertical: bv,
      userType: "General",
      plantRdc: "N/A",
    },
    {
      employeeCode: "EMP002",
      adId: "sample.plant",
      fullName: "Sample Plant User",
      email: "sample.plant@example.com",
      mobileNumber: "",
      role,
      market,
      country: "-",
      zone: "-",
      region: "-",
      territory: "-",
      businessVertical: bv,
      userType: "Plant",
      plantRdc: plant,
    },
    {
      employeeCode: "EMP003",
      adId: "sample.rdc",
      fullName: "Sample RDC User",
      email: "sample.rdc@example.com",
      mobileNumber: "",
      role,
      market,
      country: "-",
      zone: "-",
      region: "-",
      territory: "-",
      businessVertical: bv,
      userType: "RDC",
      plantRdc: rdc,
    },
    {
      employeeCode: "EMP004",
      adId: "sample.cpd",
      fullName: "Sample CPD User",
      email: "sample.cpd@example.com",
      mobileNumber: "",
      role,
      market,
      country,
      zone: "ALL",
      region: "ALL",
      territory: "ALL",
      businessVertical: bv,
      userType: "CPD",
      plantRdc: "N/A",
    },
    {
      employeeCode: "EMP005",
      adId: "sample.bcm",
      fullName: "Sample BCM User",
      email: "sample.bcm@example.com",
      mobileNumber: "",
      role,
      market,
      country,
      zone: "ALL",
      region: "ALL",
      territory: "ALL",
      businessVertical: bv,
      userType: "BCM",
      plantRdc: "N/A",
    },
  ];
};

/**
 * Picks the worksheet to upload by its header row, not its name.
 * One matching sheet is used directly; several matching sheets prompt the user,
 * defaulting to the sheet that was active when the workbook was saved.
 * Returns { ws, found } | { cancelled: true } | null (no sheet has the headers).
 */
const pickUploadWorksheet = async (wb, findHeaderRow) => {
  const candidates = (wb?.worksheets || [])
    .filter((ws) => ws && ws.state !== "hidden" && ws.state !== "veryHidden")
    .map((ws) => ({ ws, found: findHeaderRow(ws, 20) }))
    .filter((c) => c.found);

  if (!candidates.length) return null;
  if (candidates.length === 1) return candidates[0];

  const activeWs = wb.worksheets?.[wb.views?.[0]?.activeTab ?? -1];
  const defaultIdx = Math.max(0, candidates.findIndex((c) => c.ws === activeWs));
  const inputOptions = {};
  candidates.forEach((c, i) => {
    const dataRows = Math.max(0, (c.ws.actualRowCount || c.ws.rowCount || 0) - c.found.rowNumber);
    inputOptions[String(i)] = `${c.ws.name} (${dataRows} row${dataRows === 1 ? "" : "s"})`;
  });

  const { isConfirmed, value } = await Swal.fire({
    title: "Select sheet to upload",
    html: `This file has <strong>${candidates.length}</strong> sheets with user data:<br/>${candidates
      .map((c) => `<code>${escapeHtml(c.ws.name)}</code>`)
      .join(", ")}`,
    input: "select",
    inputOptions,
    inputValue: String(defaultIdx),
    showCancelButton: true,
    confirmButtonText: "Use this sheet",
  });
  if (!isConfirmed) return { cancelled: true };
  return candidates[Number(value)] || candidates[defaultIdx];
};

/** Sample rows matching MikasaPLY sheet layout (General, Plant, RDC, CPD, BCM). */
const buildMasterUploadSampleRows = () => [
  {
    employeeCode: "22113200",
    fullName: "Prajwal D",
    roleName: "Initiator",
    email: "prajwal.d@greenlam.com",
    market: "Domestic",
    country: "INDIA",
    zone: "South",
    region: "Tamil Nadu",
    territory: "ALL",
    plant: "",
    businessVertical: "MikasaPly",
    status: "Active",
    userType: "General",
  },
  {
    employeeCode: "55550034",
    fullName: "Lalatendu Rath",
    roleName: "Manufacturing Head",
    email: "lalatendu.rath@greenlam.com",
    market: "Domestic",
    country: "",
    zone: "",
    region: "",
    territory: "",
    plant: "TINDIVANAM",
    businessVertical: "MikasaPly",
    status: "Active",
    userType: "Plant",
  },
  {
    employeeCode: "EMPRDC01",
    fullName: "Sample RDC User",
    roleName: "RDC Incharge",
    email: "sample.rdc@greenlam.com",
    market: "Domestic",
    country: "",
    zone: "",
    region: "",
    territory: "",
    plant: "RDC Name",
    businessVertical: "MikasaPly",
    status: "Active",
    userType: "RDC",
  },
  {
    employeeCode: "EMPCPD01",
    fullName: "Sample CPD User",
    roleName: "HO Logistic",
    email: "sample.cpd@greenlam.com",
    market: "Domestic",
    country: "INDIA",
    zone: "ALL",
    region: "ALL",
    territory: "ALL",
    plant: "",
    businessVertical: "MikasaPly",
    status: "Active",
    userType: "CPD",
  },
  {
    employeeCode: "EMPBCM01",
    fullName: "Sample BCM User",
    roleName: "BCM",
    email: "sample.bcm@greenlam.com",
    market: "Domestic",
    country: "INDIA",
    zone: "ALL",
    region: "ALL",
    territory: "ALL",
    plant: "",
    businessVertical: "MikasaPly",
    status: "Active",
    userType: "BCM",
  },
];

const uniqueCsvJoin = (values) => {
  const seen = new Set();
  const out = [];
  for (const v of values) {
    const s = String(v || "").trim();
    if (!s) continue;
    const k = s.toUpperCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(s);
  }
  return out.join(", ");
};

/** One Excel geo/plant line per row → one submit row per email (geo tokens merged). */
const collapseMasterBulkRows = (rows) => {
  const map = new Map();
  for (const r of rows) {
    const key = String(r.email || "").trim().toLowerCase();
    if (!key) continue;
    if (!map.has(key)) {
      map.set(key, {
        ...r,
        _countries: [],
        _zones: [],
        _regions: [],
        _territories: [],
        _bvs: [],
      });
    }
    const g = map.get(key);
    if (r.plantIds && !g.plantIds) g.plantIds = r.plantIds;
    if (r.rdcIds && !g.rdcIds) g.rdcIds = r.rdcIds;
    const spec = String(r.userSpecificationType || "").trim();
    if (/^plant$/i.test(spec)) g.userSpecificationType = "Plant";
    else if (/^rdc$/i.test(spec) && g.userSpecificationType !== "Plant") {
      g.userSpecificationType = "RDC";
    } else if (
      /^cpd$/i.test(spec) &&
      g.userSpecificationType !== "Plant" &&
      g.userSpecificationType !== "RDC"
    ) {
      g.userSpecificationType = "CPD";
    } else if (
      /^bcm$/i.test(spec) &&
      g.userSpecificationType !== "Plant" &&
      g.userSpecificationType !== "RDC" &&
      g.userSpecificationType !== "CPD"
    ) {
      g.userSpecificationType = "BCM";
    }
    g._countries.push(r.country);
    g._zones.push(r.zone);
    g._regions.push(r.region);
    g._territories.push(r.territory);
    g._bvs.push(r.businessVertical);
  }
  return [...map.values()].map((g, i) => {
    const isPlant = /^plant$/i.test(g.userSpecificationType);
    const isRdc = /^rdc$/i.test(g.userSpecificationType);
    const skipGeo = isPlant || isRdc;
    const { _countries, _zones, _regions, _territories, _bvs, ...rest } = g;
    return {
      ...rest,
      rowNumber: i + 1,
      country: skipGeo ? "" : uniqueCsvJoin(_countries),
      zone: skipGeo ? "" : uniqueCsvJoin(_zones),
      region: skipGeo ? "" : uniqueCsvJoin(_regions),
      territory: skipGeo ? "" : uniqueCsvJoin(_territories),
      businessVertical: uniqueCsvJoin(_bvs) || rest.businessVertical,
      plantIds: isPlant ? rest.plantIds : "",
      rdcIds: isRdc ? rest.rdcIds || rest.plantIds : "",
    };
  });
};

const layoutUserGridDataTable = (tableEl) => {
  const wrapper = $(tableEl).closest(".dataTables_wrapper, .dt-container");
  if (!wrapper.length) return;
  const lengthEl = wrapper.find(".dt-length, .dataTables_length");
  const searchEl = wrapper.find(".dt-search, .dataTables_filter");
  if (lengthEl.length > 0 || searchEl.length > 0) {
    let topWrap = wrapper.find(".dtFltrTop");
    if (topWrap.length === 0) {
      topWrap = $("<div class='dtFltrTop d-flex flex-wrap'></div>");
      wrapper.prepend(topWrap);
    }
    lengthEl.each(function () {
      $(this).appendTo(topWrap);
    });
    searchEl.each(function () {
      $(this).appendTo(topWrap);
    });
  }
  const infoEl = wrapper.find(".dt-info, .dataTables_info");
  const pagingEl = wrapper.find(".dt-paging, .dataTables_paginate");
  if (infoEl.length > 0 || pagingEl.length > 0) {
    let bottomWrap = wrapper.find(".dtFltrBotm");
    if (bottomWrap.length === 0) {
      bottomWrap = $("<div class='dtFltrBotm d-flex flex-wrap'></div>");
      wrapper.append(bottomWrap);
    }
    infoEl.each(function () {
      $(this).appendTo(bottomWrap);
    });
    pagingEl.each(function () {
      $(this).appendTo(bottomWrap);
    });
  }
  const dtScrollDiv = $(tableEl).closest(".dt-scroll");
  if (dtScrollDiv.length > 0) {
    if (!dtScrollDiv.parent().hasClass("dtScrlWrprInner")) {
      dtScrollDiv.wrap('<div class="dtScrlWrprInner"></div>');
    }
    const outer = dtScrollDiv.parent().parent();
    if (outer.length > 0) outer.addClass("dtScrlWrpr");
  }
};

/** Export market → Zone first; Domestic → Country first (M001 / M002). */
const isExportMarketSelected = (marketCodes, markets) => {
  if (!Array.isArray(marketCodes) || !marketCodes.length) return false;
  const list = Array.isArray(markets) ? markets : [];
  return marketCodes.some((code) => {
    const c = String(code ?? "").trim().toUpperCase();
    const m = list.find((x) => getMarketCode(x).toUpperCase() === c);
    if (!m) return c === "M002";
    const name = getMarketName(m).toLowerCase();
    return name === "export" || name.includes("export") || c === "M002";
  });
};

/** API list helpers (territory-master may return data as array or wrapped). */
const extractZonesFromResponse = (response) => {
  const { ok, payload } = parseApiEnvelope(response);
  if (!ok) return [];
  if (Array.isArray(payload)) return payload;
  return payload?.zones || payload?.Zones || [];
};

const extractRegionsFromResponse = (response) => {
  const { ok, payload } = parseApiEnvelope(response);
  if (!ok) return [];
  if (Array.isArray(payload)) return payload;
  return payload?.regions || payload?.Regions || [];
};

const normalizeZoneRow = (item, fallbackCountryCode) => ({
  zoneCode: item.zoneCode || item.ZoneCode || "",
  zoneName: item.zoneName || item.ZoneName || "",
  countryCode: item.countryCode || item.CountryCode || fallbackCountryCode || "",
  countryName: item.countryName || item.CountryName || "",
  isActive: item.isActive ?? item.IsActive ?? true,
});

const resolveCountryName = (countryCode, countries = []) => {
  const cc = String(countryCode ?? "").trim();
  if (!cc) return "";
  const row = (countries || []).find(
    (c) => String(c.countryCode || c.CountryCode || "").trim() === cc
  );
  return String(row?.countryName || row?.CountryName || "").trim();
};

const formatZoneOptionLabel = (zone, countries = [], { includeCountry = true } = {}) => {
  const zoneName = String(zone.zoneName || zone.ZoneName || "").trim();
  if (!includeCountry) {
    return zoneName || zone.zoneCode || zone.ZoneCode || "";
  }
  const countryLabel =
    String(zone.countryName || zone.CountryName || "").trim() ||
    resolveCountryName(zone.countryCode || zone.CountryCode, countries);
  if (!zoneName) return countryLabel || zone.zoneCode || zone.ZoneCode || "";
  return countryLabel ? `${zoneName} (${countryLabel})` : zoneName;
};

const normalizeRegionRow = (item) => ({
  regionCode: item.regionCode || item.RegionCode || "",
  regionName: item.regionName || item.RegionName || "",
  countryCode: item.countryCode || item.CountryCode || "",
  zoneCode: item.zoneCode || item.ZoneCode || "",
  isActive: item.isActive ?? item.IsActive ?? true,
});

const normalizeTerritoryRow = (item) => ({
  territoryCode: item.territoryCode || item.TerritoryCode || "",
  territoryName: item.territoryName || item.TerritoryName || "",
  countryCode: item.countryCode || item.CountryCode || "",
  regionCode: item.regionCode || item.RegionCode || "",
  zoneCode: item.zoneCode || item.ZoneCode || "",
  isActive: item.isActive ?? item.IsActive ?? true,
});

/** Shape dropdown zone rows for findZoneOption / resolveZoneCodeForCountry. */
const toZoneLookupOptions = (zones = []) =>
  (Array.isArray(zones) ? zones : []).map((z) => ({
    id: z.zoneCode || z.ZoneCode || z.id || "",
    zoneCode: z.zoneCode || z.ZoneCode || "",
    zoneCodes: z.zoneCodes || [],
    countryCode: z.countryCode || z.CountryCode || "",
    countryCodes: z.countryCodes || [],
    zoneCodeByCountry: z.zoneCodeByCountry || {},
  }));

const resolveCanonicalZoneCode = (zones, code) => {
  const z = findZoneOption(toZoneLookupOptions(zones), code);
  return z?.id || z?.zoneCode || code;
};

const isGeoAllOrDefaultCode = (code) => {
  const s = String(code ?? "").trim().toLowerCase();
  return !s || s === "all" || s === "0000" || isUserGeoDefaultCode(code);
};

/** Omit master-data row "All" from geo dropdowns (keeps CustomMultiSelect "Select all" unchanged). */
const isOmittedGeoMasterOption = (id, text) => {
  const code = String(id ?? "").trim().toLowerCase();
  const label = String(text ?? "").trim().toLowerCase();
  return code === "all" || label === "all";
};

const toGeoSelectOption = (id, text) => {
  const safeId = String(id ?? "").trim();
  if (!safeId || isOmittedGeoMasterOption(safeId, text)) return null;
  const safeText = String(text ?? "").trim();
  return { id: safeId, text: safeText || safeId };
};

const filterOmittedGeoMasterOptions = (options) =>
  (Array.isArray(options) ? options : [])
    .map((o) => toGeoSelectOption(o?.id, o?.text))
    .filter(Boolean);

const filterOmittedGeoCodes = (codes) =>
  Array.isArray(codes)
    ? codes
        .map((c) => String(c ?? "").trim())
        .filter((c) => c && !isOmittedGeoMasterOption(c, c))
    : [];

/** Map stored tokens to PlantMaster.PlantCode (handles legacy rows that stored PlantId). */
const tokensToPlantCodes = (tokens, plants) => {
  if (!Array.isArray(tokens) || !plants?.length) return Array.isArray(tokens) ? [...tokens] : [];
  return tokens
    .map((t) => {
      const s = String(t ?? "").trim();
      if (!s) return "";
      const byCode = plants.find(
        (p) => String(p.plantCode ?? p.PlantCode ?? "").trim() === s
      );
      if (byCode) return String(byCode.plantCode ?? byCode.PlantCode ?? "").trim();
      const byId = plants.find((p) => String(p.plantId ?? p.PlantId ?? "").trim() === s);
      if (byId) return String(byId.plantCode ?? byId.PlantCode ?? "").trim() || s;
      return s;
    })
    .filter(Boolean);
};

/** Map stored tokens to RDCMaster.RdcCode (handles legacy rows that stored RdcId). */
const tokensToRdcCodes = (tokens, rdcs) => {
  if (!Array.isArray(tokens) || !rdcs?.length) return Array.isArray(tokens) ? [...tokens] : [];
  return tokens
    .map((t) => {
      const s = String(t ?? "").trim();
      if (!s) return "";
      const byCode = rdcs.find((r) => String(r.rdcCode ?? r.RdcCode ?? r.RDCCODE ?? "").trim() === s);
      if (byCode) return String(byCode.rdcCode ?? byCode.RdcCode ?? byCode.RDCCODE ?? "").trim();
      const byId = rdcs.find((r) => String(r.rdcId ?? r.RdcId ?? r.RDCID ?? "").trim() === s);
      if (byId) return String(byId.rdcCode ?? byId.RdcCode ?? byId.RDCCODE ?? "").trim() || s;
      return s;
    })
    .filter(Boolean);
};

const mergeUniqueTokens = (...tokenLists) => {
  const seen = new Set();
  const out = [];
  for (const list of tokenLists) {
    for (const t of Array.isArray(list) ? list : []) {
      const s = String(t ?? "").trim();
      if (s && !seen.has(s)) {
        seen.add(s);
        out.push(s);
      }
    }
  }
  return out;
};

/** Plant and RDC scope codes are stored in UserMaster.PlantIds (RdcIds left empty). */
const buildUserMasterPlantIdsCsv = (userSpecificationType, plantIds, rdcIds, joinCsv) => {
  if (userSpecificationType === "Plant") return joinCsv(plantIds);
  if (userSpecificationType === "RDC") return joinCsv(rdcIds);
  return "";
};

const AddEditUser = () => {
  const navigate = useNavigate();
  const { userIdCode } = useParams();
  const isEditMode = !!userIdCode;

  // Permission checks are based on the parent route MenuCode(s).
  // This supports route configs that use multiple MenuCodes (e.g., USER_VIEW + USER_MANAGE).
  const routePermissions = getRoutePermissions("/users");
  const { canAdd, canEdit } = routePermissions;
  const canSave = isEditMode ? canEdit : canAdd;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState("single"); // 'single' | 'bulk' | 'master'
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkParsing, setBulkParsing] = useState(false);
  const [user, setUser] = useState({
    userIdCode: "",
    userName: "",
    email: "",
    fullName: "",
    employeeCode: "",
    userTypeCode: "",
    adUserCode: "",
    darwinUserCode: "",
    phoneNumber: "",
    marketCodes: [],
    userSpecificationType: "General",
    plantIds: [],
    rdcIds: [],
    countryCodes: [],
    zoneCodes: [],
    regionCodes: [],
    territoryCodes: [],
    bvCodes: [],
    isActive: true,
  });

  const [dropdowns, setDropdowns] = useState({
    userTypes: [],
    markets: [],
    countries: [],
    zones: [],
    regions: [],
    territories: [],
    businessVerticals: [],
    allBusinessVerticals: [],
    plants: [],
    rdcs: [],
  });

  // Use ref to store current dropdowns to avoid closure issues in event handlers
  const dropdownsRef = useRef(dropdowns);
  /** After loadUser: scope ALL — expand to every loaded option in cascade dropdowns. */
  const pendingCountryExpandAllRef = useRef(false);
  const pendingZoneExpandAllRef = useRef(false);
  const pendingRegionExpandAllRef = useRef(false);
  const pendingTerritoryExpandAllRef = useRef(false);
  const bulkTableRef = useRef(null);
  const bulkDataTableInstanceRef = useRef(null);
  const bulkMastersRef = useRef(null);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    if (!canSave) {
      Swal.fire({
        title: "Access Denied",
        text: `You do not have permission to ${
          isEditMode ? "edit" : "add"
        } users.`,
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => {
        navigate("/users");
      });
      return;
    }
    loadDropdowns();
    if (isEditMode) {
      loadUser();
    } else {
      // Auto-generate User ID Code for new users
      loadNextUserIdCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdCode, canSave]);

  // Update dropdownsRef when dropdowns state changes
  useEffect(() => {
    dropdownsRef.current = dropdowns;
  }, [dropdowns]);

  const unwrapPagedList = (response, keys) => {
    const payload = response?.data ?? response?.Data ?? response ?? {};
    for (const k of keys) {
      if (Array.isArray(payload[k])) return payload[k];
    }
    if (Array.isArray(payload)) return payload;
    const inner = payload.data ?? payload.Data;
    if (Array.isArray(inner)) return inner;
    return [];
  };

  const ensureBulkMastersLoaded = async () => {
    if (bulkMastersRef.current) return bulkMastersRef.current;
    const dd = dropdownsRef.current || {};
    const [catRes, zoneRes, regionRes, terrRes] = await Promise.allSettled([
      categoryMasterAPI.getPaged({
        isActive: true,
        pageNumber: 1,
        pageSize: 8000,
      }),
      zoneMasterAPI.getPaged({
        status: "active",
        pageNumber: 1,
        pageSize: 8000,
      }),
      regionMasterAPI.getPaged({
        status: "active",
        pageNumber: 1,
        pageSize: 8000,
      }),
      territoryMasterAPI.getPaged({
        status: "active",
        pageNumber: 1,
        pageSize: 8000,
      }),
    ]);
    const take = (res, keys) =>
      res.status === "fulfilled" ? unwrapPagedList(res.value, keys) : [];
    const masters = {
      markets: dd.markets || [],
      countries: dd.countries || [],
      userTypes: dd.userTypes || [],
      plants: dd.plants || [],
      rdcs: dd.rdcs || [],
      businessVerticals: take(catRes, ["categories", "Categories"]),
      zones: take(zoneRes, ["zones", "Zones"]),
      regions: take(regionRes, ["regions", "Regions"]),
      territories: take(terrRes, ["territories", "Territories"]),
      existingUsers: [],
      existingUsersLoaded: false,
    };
    bulkMastersRef.current = masters;
    return masters;
  };

  const ensureExistingUsersLoaded = async () => {
    const masters = bulkMastersRef.current || (await ensureBulkMastersLoaded());
    if (masters.existingUsersLoaded) {
      return masters.existingUsers || [];
    }
    try {
      const usersRes = await userAPI.getAll({ status: "all" });
      const users = usersRes?.data ?? usersRes?.Data ?? [];
      masters.existingUsers = Array.isArray(users) ? users : [];
    } catch (err) {
      console.warn("Could not load existing users for bulk update checks.", err);
      masters.existingUsers = [];
    }
    masters.existingUsersLoaded = true;
    bulkMastersRef.current = masters;
    return masters.existingUsers;
  };

  const getBulkValidationOptions = async (allowMultiGeoRows = false) => {
    try {
      const masters = await ensureBulkMastersLoaded();
      const existingUsers = await ensureExistingUsersLoaded();
      return { allowMultiGeoRows, masters, existingUsers };
    } catch (err) {
      console.warn("Bulk master lists unavailable; required-field checks only.", err);
      const dd = dropdownsRef.current || {};
      return {
        allowMultiGeoRows,
        existingUsers: bulkMastersRef.current?.existingUsers || [],
        masters: {
          markets: dd.markets || [],
          countries: dd.countries || [],
          userTypes: dd.userTypes || [],
          plants: dd.plants || [],
          rdcs: dd.rdcs || [],
          businessVerticals: dd.businessVerticals || [],
          zones: dd.zones || [],
          regions: dd.regions || [],
          territories: dd.territories || [],
        },
      };
    }
  };

  const switchUploadMode = (mode) => {
    // CSRP Bulk Upload is hidden — only single | master
    const next = mode === "bulk" ? "single" : mode;
    if (next === uploadMode) return;
    setUploadMode(next);
    setBulkFile(null);
    setBulkRows([]);
  };

  // Bulk preview grid — same DataTable design as /csrp/users
  useEffect(() => {
    const destroyBulkTable = () => {
      if (bulkDataTableInstanceRef.current) {
        disposeUserGeoTooltips(bulkTableRef.current);
        try {
          bulkDataTableInstanceRef.current.destroy();
        } catch {
          /* already destroyed */
        }
        bulkDataTableInstanceRef.current = null;
      }
    };

    if (uploadMode !== "master" || !bulkTableRef.current) {
      destroyBulkTable();
      return undefined;
    }

    destroyBulkTable();

    try {
      if (!$.fn.DataTable) {
        DataTable($);
      }

      bulkDataTableInstanceRef.current = $(bulkTableRef.current).DataTable({
        data: bulkRows,
        paging: true,
        searching: true,
        info: true,
        lengthChange: true,
        lengthMenu: [5, 10, 25, 50, 100],
        dom: "lfrtip",
        ordering: true,
        destroy: true,
        scrollX: true,
        autoWidth: false,
        deferRender: true,
        pageLength: 50,
        language: {
          emptyTable:
            "Select a master Excel file to preview rows (MikasaPLY / master-data layout).",
        },
        columns: [
          {
            title: "SNo.",
            data: null,
            render: (d, type, row, meta) => (meta.row + 1).toString(),
            className: "text-center",
          },
          {
            title: "Employee Code",
            data: "employeeCode",
            render: (d) => d || "-",
          },
          {
            title: "AD ID",
            data: "adId",
            render: (d) => d || "-",
          },
          {
            title: "User Full Name",
            data: "fullName",
            render: (d) => d || "-",
          },
          {
            title: "Employee email ID",
            data: "email",
            render: (d) => d || "-",
          },
          {
            title: "Mobile Number",
            data: "mobileNumber",
            render: (d) => d || "-",
          },
          {
            title: "Role",
            data: "role",
            render: (d) => d || "-",
          },
          {
            title: "Market",
            data: "market",
            render: (d) => d || "-",
          },
          {
            title: "Country",
            data: "country",
            render: (d, type) => renderGeoTooltipCell(d || "-", type),
          },
          {
            title: "Zone",
            data: "zone",
            render: (d, type) => renderGeoTooltipCell(d || "-", type),
          },
          {
            title: "Region",
            data: "region",
            render: (d, type) => renderGeoTooltipCell(d || "-", type),
          },
          {
            title: "Territory",
            data: "territory",
            render: (d, type) => renderGeoTooltipCell(d || "-", type),
          },
          {
            title: "Business Vertical",
            data: "businessVertical",
            render: (d, type) => renderGeoTooltipCell(d || "-", type),
          },
          {
            title: "User Type",
            data: "userSpecificationType",
            render: (d, type, row) =>
              renderGeoTooltipCell(formatBulkUserType(row), type),
          },
          {
            title: "Plant / RDC",
            data: null,
            render: (d, type, row) =>
              renderGeoTooltipCell(formatBulkPlantOrRdc(row), type),
          },
          ...(uploadMode === "master"
            ? [
                {
                  title: "Action",
                  data: "bulkAction",
                  className: "text-center",
                  render: (d) => (d === "Update" ? "Update" : "Insert"),
                },
              ]
            : []),
        ],
        initComplete: function () {
          try {
            layoutUserGridDataTable(bulkTableRef.current);
          } catch (error) {
            console.error("Error in bulk preview initComplete:", error);
          }
        },
        drawCallback: function () {
          try {
            initUserGeoTooltips(bulkTableRef.current);
          } catch (error) {
            console.error("Error initializing bulk preview tooltips:", error);
          }
        },
      });
    } catch (error) {
      console.error("Error initializing bulk preview DataTable:", error);
    }

    return () => {
      destroyBulkTable();
    };
  }, [uploadMode, bulkRows]);

  const isExportCascade = useMemo(
    () => isExportMarketSelected(user.marketCodes, dropdowns.markets),
    [user.marketCodes, dropdowns.markets]
  );

  /** Export flow: countries limited to those linked to selected zone(s). */
  const exportCountryOptions = useMemo(() => {
    if (!isExportCascade) return dropdowns.countries || [];
    const zSel = user.zoneCodes || [];
    const zones = dropdowns.zones || [];
    if (!zSel.length) return [];
    const codes = new Set();
    const lookup = toZoneLookupOptions(zones);
    zSel.forEach((zc) => {
      const z = findZoneOption(lookup, zc);
      if (!z) return;
      if (Array.isArray(z.countryCodes) && z.countryCodes.length) {
        z.countryCodes.forEach((cc) => {
          const c = String(cc ?? "").trim();
          if (c) codes.add(c);
        });
      } else if (z.countryCode) {
        codes.add(String(z.countryCode).trim());
      }
    });
    return (dropdowns.countries || []).filter((c) =>
      codes.has(String(c.countryCode || c.CountryCode || "").trim())
    );
  }, [isExportCascade, user.zoneCodes, dropdowns.zones, dropdowns.countries]);

  const loadNextUserIdCode = async () => {
    try {
      const response = await userAPI.getNextCode();
      if (response && response.success && response.data) {
        setUser((prev) => ({ ...prev, userIdCode: response.data }));
      }
    } catch (e) {
      console.error("Error loading next user ID code:", e);
      // Fallback to USR001 if API fails
      setUser((prev) => ({ ...prev, userIdCode: "USR001" }));
    }
  };

  const loadDropdowns = async () => {
    try {
      setLoading(true);
      const results = await Promise.allSettled([
        masterDataAPI.getUserTypes(),
        masterDataAPI.getMarkets("", 1, 500),
        masterDataAPI.getCountries(),
        plantMasterAPI.getPaged({ status: "active", pageNumber: 1, pageSize: 8000 }),
        rdcMasterAPI.getPaged({ status: "active", pageNumber: 1, pageSize: 8000 }),
        categoryMasterAPI.getPaged({
          isActive: true,
          pageNumber: 1,
          pageSize: 8000,
        }),
      ]);

      const [userTypesRes, marketsRes, countriesRes, plantsRes, rdcsRes, allCatRes] = results.map((r, idx) => {
        if (r.status === "fulfilled") return r.value;
        console.error("Dropdown API failed at index", idx, r.reason);
        return null;
      });

      const plantsPayload = plantsRes?.data;
      const plantsList =
        plantsPayload?.Plants ||
        plantsPayload?.plants ||
        plantsPayload?.data?.Plants ||
        [];
      const rdcsPayload = rdcsRes?.data;
      const rdcsList =
        rdcsPayload?.Rdcs ||
        rdcsPayload?.rdcs ||
        rdcsPayload?.data?.Rdcs ||
        [];

      const allCatPayload = allCatRes?.data ?? allCatRes?.Data ?? {};
      const allCats =
        allCatPayload.categories ||
        allCatPayload.Categories ||
        allCatPayload.data?.Categories ||
        allCatPayload.data?.categories ||
        [];

      const newDropdowns = {
        userTypes: userTypesRes?.data || [],
        markets: marketsRes?.data?.markets || marketsRes?.data?.Markets || marketsRes?.data || [],
        countries: countriesRes?.data || [],
        zones: [],
        regions: [],
        territories: [],
        // Business Vertical options come from CATEGORYMASTER (CATGCODE/CATGNAME) loaded per Market.
        businessVerticals: [],
        allBusinessVerticals: Array.isArray(allCats) ? allCats : [],
        plants: Array.isArray(plantsList) ? plantsList : [],
        rdcs: Array.isArray(rdcsList) ? rdcsList : [],
      };
      
      setDropdowns(newDropdowns);
      dropdownsRef.current = newDropdowns;
      
      // Add mode: select a default Market so Business Vertical can populate immediately.
      if (!isEditMode) {
        const markets = newDropdowns.markets || [];
        const pickDefaultMarket = () => {
          const active =
            markets.find((m) => (m.isActive ?? m.ISACTIVE ?? m.IsActive) === true) ||
            markets.find((m) => (m.isActive ?? m.ISACTIVE ?? m.IsActive) === 1);
          const fallback = markets[0];
          const m = active || fallback;
          return m ? (m.marketCode || m.MARKETCODE || m.MarketCode || "") : "";
        };
        const defaultMarketCode = pickDefaultMarket();
        if (defaultMarketCode) {
          setUser((prev) =>
            prev.marketCodes?.length
              ? prev
              : { ...prev, marketCodes: [defaultMarketCode] }
          );
          // Load Business Verticals for default market
          try {
            const catRes = await categoryMasterAPI.getPaged({
              marketCode: defaultMarketCode,
              isActive: true,
              pageNumber: 1,
              pageSize: 5000,
            });
            const cats =
              catRes?.data?.Categories ||
              catRes?.data?.categories ||
              catRes?.data?.data?.Categories ||
              [];
            setDropdowns((prev) => ({ ...prev, businessVerticals: Array.isArray(cats) ? cats : [] }));
          } catch (e) {
            console.error("Failed to load categories for market", defaultMarketCode, e);
          }
        }
      }

      setError(null);
    } catch (e) {
      console.error("Error loading dropdowns:", e);
      setError("Failed to load dropdown data. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const loadUser = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getById(userIdCode);
      if (response && response.success && response.data) {
        const u = response.data;
        const splitCsv = (v) =>
          typeof v === "string"
            ? v
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : Array.isArray(v)
              ? v.filter(Boolean)
              : [];

        const mcRaw = u.marketCodes ?? u.MarketCodes;
        const marketCodesArr = Array.isArray(mcRaw)
          ? mcRaw.map((x) => String(x || "").trim()).filter(Boolean)
          : splitCsv(u.marketCode || u.MarketCode || "");

        setUser((prev) => {
          const userSpecType = normalizeUserSpecificationType(
            u.userSpecificationType ?? u.UserSpecificationType
          );
          const next = {
            ...prev,
            ...u,
            marketCodes: marketCodesArr,
            userSpecificationType: userSpecType,
            plantIds:
              userSpecType === "Plant"
                ? splitCsv(u.plantIds ?? u.PlantIds ?? "")
                : [],
            rdcIds:
              userSpecType === "RDC"
                ? mergeUniqueTokens(
                    splitCsv(u.plantIds ?? u.PlantIds ?? ""),
                    splitCsv(u.rdcIds ?? u.RdcIds ?? "")
                  )
                : [],
            countryCodes: (() => {
              if (userSpecType === "Plant" || userSpecType === "RDC") return [];
              const picked = pickUserGeoCodesFromApi(u, "country");
              if (picked.expandAll) pendingCountryExpandAllRef.current = true;
              return picked.codes;
            })(),
            zoneCodes: (() => {
              if (userSpecType === "Plant" || userSpecType === "RDC") return [];
              const picked = pickUserGeoCodesFromApi(u, "zone");
              if (picked.expandAll) pendingZoneExpandAllRef.current = true;
              return picked.codes;
            })(),
            regionCodes: (() => {
              if (userSpecType === "Plant" || userSpecType === "RDC") return [];
              const picked = pickUserGeoCodesFromApi(u, "region");
              if (picked.expandAll) pendingRegionExpandAllRef.current = true;
              return picked.codes;
            })(),
            territoryCodes: (() => {
              if (userSpecType === "Plant" || userSpecType === "RDC") return [];
              const picked = pickUserGeoCodesFromApi(u, "territory");
              if (picked.expandAll) pendingTerritoryExpandAllRef.current = true;
              return picked.codes;
            })(),
            countryScopeMode: u.countryScopeMode ?? u.CountryScopeMode ?? "",
            zoneScopeMode: u.zoneScopeMode ?? u.ZoneScopeMode ?? "",
            regionScopeMode: u.regionScopeMode ?? u.RegionScopeMode ?? "",
            territoryScopeMode: u.territoryScopeMode ?? u.TerritoryScopeMode ?? "",
            bvCodes: splitCsv(u.catgCode || u.CATGCODE || u.bvCode || u.BVCode),
          };
          const fromMapping = applyLocationMappingsToForm(u);
          if (fromMapping) {
            next.countryCodes = fromMapping.countryCodes;
            next.zoneCodes = fromMapping.zoneCodes;
            next.regionCodes = fromMapping.regionCodes;
            next.territoryCodes = fromMapping.territoryCodes;
            next.locationMappings = fromMapping.locationMappings;
            // Mapping rows use 0000 for Excel ALL — keep expand-all for Region/Territory cascade.
            const mappingAllAt = (codeKeys) =>
              (fromMapping.locationMappings || []).some((m) =>
                codeKeys.some((k) => {
                  const v = String(m?.[k] ?? "").trim().toLowerCase();
                  return v === "0000" || v === "all";
                })
              );
            if (!fromMapping.regionCodes?.length && mappingAllAt(["regionCode", "RegionCode"])) {
              pendingRegionExpandAllRef.current = true;
              if (!next.regionScopeMode) next.regionScopeMode = "ALL";
            }
            if (
              !fromMapping.territoryCodes?.length &&
              mappingAllAt(["territoryCode", "TerritoryCode", "TERRITORYCODE"])
            ) {
              pendingTerritoryExpandAllRef.current = true;
              if (!next.territoryScopeMode) next.territoryScopeMode = "ALL";
            }
          }
          delete next.marketCode;
          delete next.MarketCode;
          return next;
        });
      } else {
        Swal.fire("Error", "User not found", "error");
        navigate("/users");
      }
    } catch (e) {
      console.error("Error loading user:", e);
      Swal.fire("Error", "Failed to load user details", "error");
      navigate("/users");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setUser((prev) => {
      // When Market changes: keep existing BV codes that still belong to remaining markets.
      // Adding Export beside Domestic must NOT wipe Domestic categories (add-on merge).
      if (field === "marketCodes") {
        const nextMarkets = Array.isArray(value) ? value : [];
        const prevBv = Array.isArray(prev.bvCodes) ? prev.bvCodes : [];
        const bvMeta = dropdownsRef.current.businessVerticals || [];
        const keepBv = prevBv.filter((code) => {
          const c = String(code || "").trim().toUpperCase();
          if (!c) return false;
          const row = bvMeta.find(
            (x) =>
              String(x.catgCode || x.CATGCODE || x.CatgCode || "")
                .trim()
                .toUpperCase() === c
          );
          if (!row) return true; // keep until catalog reloads
          const mk = String(row.marketCode || row.MARKETCODE || row.MarketCode || "")
            .trim()
            .toUpperCase();
          if (!mk) return true;
          return nextMarkets.some((m) => String(m || "").trim().toUpperCase() === mk);
        });
        return {
          ...prev,
          marketCodes: nextMarkets,
          bvCodes: keepBv,
          countryCodes: [],
          zoneCodes: [],
          regionCodes: [],
          territoryCodes: [],
        };
      }
      if (field === "userSpecificationType") {
        const spec = normalizeUserSpecificationType(value);
        const showGeo = !isPlantOrRdcSpec(spec);
        return {
          ...prev,
          userSpecificationType: spec,
          plantIds: [],
          rdcIds: [],
          ...(showGeo
            ? {}
            : {
                countryCodes: [],
                zoneCodes: [],
                regionCodes: [],
                territoryCodes: [],
              }),
        };
      }
      const exportCascade = isExportMarketSelected(
        prev.marketCodes,
        dropdownsRef.current.markets || []
      );

      // Geographic cascade — Domestic: Country → Zone → Region → Territory
      // Export: Zone → Country → Region → Territory
      if (field === "countryCodes") {
        pendingTerritoryExpandAllRef.current = false;
        const nextCountry = filterOmittedGeoCodes(value);
        if (exportCascade) {
          return {
            ...prev,
            countryCodes: nextCountry,
            regionCodes: [],
            territoryCodes: [],
          };
        }
        return {
          ...prev,
          countryCodes: nextCountry,
          zoneCodes: [],
          regionCodes: [],
          territoryCodes: [],
        };
      }
      if (field === "zoneCodes") {
        pendingTerritoryExpandAllRef.current = false;
        const nextZone = filterOmittedGeoCodes(value);
        if (exportCascade) {
          return {
            ...prev,
            zoneCodes: nextZone,
            countryCodes: [],
            regionCodes: [],
            territoryCodes: [],
          };
        }
        return {
          ...prev,
          zoneCodes: nextZone,
          regionCodes: [],
          territoryCodes: [],
        };
      }
      if (field === "regionCodes") {
        pendingTerritoryExpandAllRef.current = false;
        return {
          ...prev,
          regionCodes: filterOmittedGeoCodes(value),
          territoryCodes: [],
        };
      }
      return { ...prev, [field]: value };
    });
  };

  // Reload Business Vertical list when selected Market(s) change — merge CATEGORYMASTER rows for all markets
  useEffect(() => {
    const codes = user.marketCodes || [];
    if (!codes.length) {
      setDropdowns((prev) => ({ ...prev, businessVerticals: [] }));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const merged = [];
        const seen = new Set();
        for (const marketCode of codes) {
          const catRes = await categoryMasterAPI.getPaged({
            marketCode,
            isActive: true,
            pageNumber: 1,
            pageSize: 5000,
          });
          const cats =
            catRes?.data?.Categories ||
            catRes?.data?.categories ||
            catRes?.data?.data?.Categories ||
            [];
          if (Array.isArray(cats)) {
            for (const c of cats) {
              const id = c.catgCode || c.CATGCODE || c.CatgCode || "";
              const key = String(id).trim().toUpperCase();
              if (key && !seen.has(key)) {
                seen.add(key);
                merged.push(c);
              }
            }
          }
        }
        if (!cancelled) {
          setDropdowns((prev) => ({ ...prev, businessVerticals: merged }));
        }
      } catch (e) {
        console.error("Failed to load categories for markets", codes, e);
        if (!cancelled) {
          setDropdowns((prev) => ({ ...prev, businessVerticals: [] }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(user.marketCodes || []).join("|")]);

  // Export: load all zones first (no country filter) — one row per zone name
  useEffect(() => {
    if (!isExportCascade) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await territoryMasterAPI.getZones();
        const raw = extractZonesFromResponse(res);
        const active = Array.isArray(raw)
          ? raw.filter((z) => z.isActive !== false && z.IsActive !== false)
          : [];
        const grouped = mapExportZonesToOptions(active, dropdownsRef.current.countries || []);
        const zones = grouped.map((o) => ({
          zoneCode: o.id,
          ZoneCode: o.id,
          zoneName: o.zoneName,
          ZoneName: o.zoneName,
          countryCode: o.countryCode,
          CountryCode: o.countryCode,
          countryName: o.countryName,
          CountryName: o.countryName,
          countryCodes: o.countryCodes || [],
          zoneCodes: o.zoneCodes || [],
          zoneCodeByCountry: o.zoneCodeByCountry || {},
        }));
        if (!cancelled) {
          setDropdowns((prev) => ({
            ...prev,
            zones,
            regions: [],
            territories: [],
          }));
          // Remap stored ZoneMaster codes onto grouped canonical ids (e.g. APAC once).
          setUser((prev) => {
            const nextZones = (prev.zoneCodes || []).map((code) =>
              resolveCanonicalZoneCode(zones, code)
            );
            const same =
              nextZones.length === (prev.zoneCodes || []).length &&
              nextZones.every((c, i) => c === prev.zoneCodes[i]);
            return same ? prev : { ...prev, zoneCodes: nextZones };
          });
        }
      } catch (e) {
        console.error("Failed to load zones (export market)", e);
        if (!cancelled) {
          setDropdowns((prev) => ({ ...prev, zones: [], regions: [], territories: [] }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isExportCascade]);

  // Domestic: zones for selected countries — GET /api/territory-master/zones?countryCode=
  useEffect(() => {
    if (isExportCascade) return;
    const ccs = user.countryCodes || [];
    if (!ccs.length) {
      setDropdowns((prev) => ({ ...prev, zones: [], regions: [], territories: [] }));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const merged = [];
        for (const cc of ccs) {
          const res = await territoryMasterAPI.getZones(cc);
          const raw = extractZonesFromResponse(res);
          const active = Array.isArray(raw)
            ? raw.filter((z) => z.isActive !== false && z.IsActive !== false)
            : [];
          active.forEach((z) => merged.push(normalizeZoneRow(z, cc)));
        }
        const byZone = new Map();
        merged.forEach((z) => {
          if (z.zoneCode) byZone.set(z.zoneCode, z);
        });
        if (!cancelled) {
          setDropdowns((prev) => ({
            ...prev,
            zones: [...byZone.values()],
            regions: [],
            territories: [],
          }));
        }
      } catch (e) {
        console.error("Failed to load zones for countries", e);
        if (!cancelled) {
          setDropdowns((prev) => ({ ...prev, zones: [], regions: [], territories: [] }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isExportCascade, user.countryCodes]);

  // Domestic: regions for selected zones
  useEffect(() => {
    if (isExportCascade) return;
    const zSel = user.zoneCodes || [];
    const zones = dropdownsRef.current.zones || [];
    if (!zSel.length || !zones.length) {
      setDropdowns((prev) => ({ ...prev, regions: [], territories: [] }));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const merged = [];
        for (const zoneCode of zSel) {
          const zrow = zones.find((x) => (x.zoneCode || x.ZoneCode) === zoneCode);
          const cc = zrow?.countryCode || zrow?.CountryCode;
          if (!cc || !zoneCode) continue;
          const res = await territoryMasterAPI.getRegions(cc, zoneCode);
          const raw = extractRegionsFromResponse(res);
          const active = Array.isArray(raw)
            ? raw.filter((r) => r.isActive !== false && r.IsActive !== false)
            : [];
          active.forEach((r) => merged.push(normalizeRegionRow(r)));
        }
        const byReg = new Map();
        merged.forEach((r) => {
          if (r.regionCode) byReg.set(r.regionCode, r);
        });
        if (!cancelled) {
          setDropdowns((prev) => ({
            ...prev,
            regions: [...byReg.values()],
            territories: [],
          }));
        }
      } catch (e) {
        console.error("Failed to load regions for zones", e);
        if (!cancelled) {
          setDropdowns((prev) => ({ ...prev, regions: [], territories: [] }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isExportCascade, user.zoneCodes, user.countryCodes, dropdowns.zones]);

  // Export: regions for selected zone(s) + country(ies)
  useEffect(() => {
    if (!isExportCascade) return;
    const zSel = (user.zoneCodes || []).filter((c) => !isGeoAllOrDefaultCode(c));
    const cSel = (user.countryCodes || []).filter((c) => !isGeoAllOrDefaultCode(c));
    const zones = dropdownsRef.current.zones || [];
    if (!zSel.length || !cSel.length || !zones.length) {
      setDropdowns((prev) => ({ ...prev, regions: [], territories: [] }));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const merged = [];
        const lookup = toZoneLookupOptions(zones);
        for (const zoneOptionId of zSel) {
          const zrow = findZoneOption(lookup, zoneOptionId);
          const linkedCountries = Array.isArray(zrow?.countryCodes)
            ? zrow.countryCodes.map((c) => String(c).trim()).filter(Boolean)
            : zrow?.countryCode
              ? [String(zrow.countryCode).trim()]
              : [];
          for (const cc of cSel) {
            if (linkedCountries.length && !linkedCountries.includes(String(cc).trim())) {
              continue;
            }
            const mappedZone = resolveZoneCodeForCountry(zoneOptionId, cc, lookup);
            const zoneCodesToTry = [];
            const addTry = (zc) => {
              const v = String(zc ?? "").trim();
              if (v && !zoneCodesToTry.includes(v)) zoneCodesToTry.push(v);
            };
            // Prefer country-specific ZoneMaster code (Indonesia→APAC for ID, etc.).
            if (zrow?.zoneCodeByCountry?.[cc]) {
              addTry(zrow.zoneCodeByCountry[cc]);
            } else if (mappedZone) {
              addTry(mappedZone);
            }
            // Fallback: try every linked zone code for this country (SP filters both).
            if (!zrow?.zoneCodeByCountry?.[cc] && Array.isArray(zrow?.zoneCodes)) {
              zrow.zoneCodes.forEach(addTry);
            }
            if (!zoneCodesToTry.length) addTry(zoneOptionId);

            for (const zoneCode of zoneCodesToTry) {
              const res = await territoryMasterAPI.getRegions(cc, zoneCode);
              const raw = extractRegionsFromResponse(res);
              const active = Array.isArray(raw)
                ? raw.filter((r) => r.isActive !== false && r.IsActive !== false)
                : [];
              active.forEach((r) => merged.push(normalizeRegionRow(r)));
              // Stop after first successful country+zone hit when we had a direct mapping.
              if (active.length && zrow?.zoneCodeByCountry?.[cc]) break;
            }
          }
        }
        const byReg = new Map();
        merged.forEach((r) => {
          if (r.regionCode) byReg.set(r.regionCode, r);
        });
        if (!cancelled) {
          setDropdowns((prev) => ({
            ...prev,
            regions: [...byReg.values()],
            territories: [],
          }));
        }
      } catch (e) {
        console.error("Failed to load regions (export market)", e);
        if (!cancelled) {
          setDropdowns((prev) => ({ ...prev, regions: [], territories: [] }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isExportCascade, user.zoneCodes, user.countryCodes, dropdowns.zones]);

  // Territories for selected regions — GET /api/territory-master (paged)
  useEffect(() => {
    const rSel = (user.regionCodes || []).filter((c) => !isGeoAllOrDefaultCode(c));
    const regions = dropdownsRef.current.regions || [];
    const zones = dropdownsRef.current.zones || [];
    const cSel = (user.countryCodes || []).filter((c) => !isGeoAllOrDefaultCode(c));
    const zSel = (user.zoneCodes || []).filter((c) => !isGeoAllOrDefaultCode(c));
    if (!rSel.length || !regions.length) {
      setDropdowns((prev) => ({ ...prev, territories: [] }));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const merged = [];
        const lookup = toZoneLookupOptions(zones);
        for (const rc of rSel) {
          const row = regions.find(
            (x) => String(x.regionCode || x.RegionCode || "").trim() === String(rc).trim()
          );
          if (!row) continue;
          let cc = String(row.countryCode || row.CountryCode || "").trim();
          let zc = String(row.zoneCode || row.ZoneCode || "").trim();
          // Export grouped zones: region row may lack zone — resolve from selected zone + country.
          if (!cc && cSel.length === 1) cc = cSel[0];
          if (!zc && zSel.length) {
            const zoneId = zSel[0];
            zc = resolveZoneCodeForCountry(zoneId, cc, lookup) || zoneId;
          }
          if (!cc || !zc || !rc) continue;
          const res = await territoryMasterAPI.getPaged({
            countryCode: cc,
            zoneCode: zc,
            regionCode: rc,
            status: "active",
            pageNumber: 1,
            pageSize: 5000,
          });
          const { ok, payload } = parseApiEnvelope(res);
          const items = ok && payload
            ? payload.territories || payload.Territories || []
            : [];
          const active = Array.isArray(items)
            ? items.filter((t) => t.isActive !== false && t.IsActive !== false)
            : [];
          active.forEach((t) => merged.push(normalizeTerritoryRow(t)));
        }
        const byT = new Map();
        merged.forEach((t) => {
          if (t.territoryCode) byT.set(t.territoryCode, t);
        });
        if (!cancelled) {
          setDropdowns((prev) => ({ ...prev, territories: [...byT.values()] }));
        }
      } catch (e) {
        console.error("Failed to load territories for regions", e);
        if (!cancelled) {
          setDropdowns((prev) => ({ ...prev, territories: [] }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user.regionCodes, user.zoneCodes, user.countryCodes, dropdowns.regions, dropdowns.zones]);

  useEffect(() => {
    if (!pendingCountryExpandAllRef.current) return;
    if (isExportCascade && !(user.zoneCodes?.length)) return;
    const list = isExportCascade ? exportCountryOptions : dropdowns.countries || [];
    if (!list.length) return;
    const allIds = filterOmittedGeoCodes(
      list.map((c) => c.countryCode || c.CountryCode)
    );
    if (!allIds.length) return;
    pendingCountryExpandAllRef.current = false;
    setUser((prev) => ({ ...prev, countryCodes: allIds }));
  }, [dropdowns.countries, exportCountryOptions, isExportCascade, user.zoneCodes]);

  useEffect(() => {
    if (!pendingZoneExpandAllRef.current) return;
    const list = dropdowns.zones || [];
    if (!list.length) return;
    const allIds = filterOmittedGeoCodes(list.map((z) => z.zoneCode || z.ZoneCode));
    if (!allIds.length) return;
    pendingZoneExpandAllRef.current = false;
    setUser((prev) => ({ ...prev, zoneCodes: allIds }));
  }, [dropdowns.zones]);

  useEffect(() => {
    if (isExportCascade && !(user.countryCodes?.length)) return;
    const list = dropdowns.regions || [];
    if (!list.length) return;
    const scopeAll =
      pendingRegionExpandAllRef.current ||
      String(user.regionScopeMode || "").toUpperCase() === "ALL";
    if (!scopeAll) return;
    const allIds = filterOmittedGeoCodes(list.map((r) => r.regionCode || r.RegionCode));
    if (!allIds.length) return;
    const same =
      allIds.length === (user.regionCodes || []).length &&
      allIds.every((id) => (user.regionCodes || []).includes(id));
    if (same) {
      pendingRegionExpandAllRef.current = false;
      return;
    }
    pendingRegionExpandAllRef.current = false;
    setUser((prev) => ({ ...prev, regionCodes: allIds }));
    if (
      pendingTerritoryExpandAllRef.current ||
      String(user.territoryScopeMode || "").toUpperCase() === "ALL"
    ) {
      pendingTerritoryExpandAllRef.current = true;
    }
  }, [
    dropdowns.regions,
    isExportCascade,
    user.countryCodes,
    user.regionScopeMode,
    user.regionCodes,
    user.territoryScopeMode,
  ]);

  // After regions reload for Export (grouped zone remap), drop stale non-ALL region codes.
  useEffect(() => {
    if (!isExportCascade) return;
    if (pendingRegionExpandAllRef.current) return;
    if (String(user.regionScopeMode || "").toUpperCase() === "ALL") return;
    const list = dropdowns.regions || [];
    if (!list.length) return;
    const valid = new Set(
      list.map((r) => String(r.regionCode || r.RegionCode || "").trim()).filter(Boolean)
    );
    const current = (user.regionCodes || []).filter((c) => !isGeoAllOrDefaultCode(c));
    if (!current.length) return;
    const next = current.filter((c) => valid.has(String(c).trim()));
    if (next.length === current.length) return;
    setUser((prev) => ({ ...prev, regionCodes: next, territoryCodes: [] }));
  }, [isExportCascade, dropdowns.regions, user.regionCodes, user.regionScopeMode]);

  // Expand "all territories" sentinel into full checkbox list once Region→Territory options are loaded.
  useEffect(() => {
    const list = dropdowns.territories || [];
    if (!list.length) return;
    const scopeAll =
      pendingTerritoryExpandAllRef.current ||
      String(user.territoryScopeMode || "").toUpperCase() === "ALL";
    if (!scopeAll) return;
    const allIds = filterOmittedGeoCodes(
      list.map((t) => t.territoryCode || t.TerritoryCode)
    );
    if (!allIds.length) return;
    const same =
      allIds.length === (user.territoryCodes || []).length &&
      allIds.every((id) => (user.territoryCodes || []).includes(id));
    if (same) {
      pendingTerritoryExpandAllRef.current = false;
      return;
    }
    pendingTerritoryExpandAllRef.current = false;
    setUser((prev) => ({ ...prev, territoryCodes: allIds }));
  }, [dropdowns.territories, user.territoryScopeMode, user.territoryCodes]);

  // After Plant master loads, align edit form values to PlantCode (DB may still have legacy PlantId CSV).
  useEffect(() => {
    if (!isEditMode) return;
    if (user.userSpecificationType !== "Plant") return;
    const plants = dropdowns.plants || [];
    if (!plants.length) return;
    setUser((prev) => {
      const next = tokensToPlantCodes(prev.plantIds, plants);
      if (JSON.stringify(next) === JSON.stringify(prev.plantIds || [])) return prev;
      return { ...prev, plantIds: next };
    });
  }, [isEditMode, user.userSpecificationType, dropdowns.plants]);

  // After RDC master loads, align edit form values to RdcCode (legacy RdcId CSV).
  useEffect(() => {
    if (!isEditMode) return;
    if (user.userSpecificationType !== "RDC") return;
    const rdcs = dropdowns.rdcs || [];
    if (!rdcs.length) return;
    setUser((prev) => {
      const next = tokensToRdcCodes(prev.rdcIds, rdcs);
      if (JSON.stringify(next) === JSON.stringify(prev.rdcIds || [])) return prev;
      return { ...prev, rdcIds: next };
    });
  }, [isEditMode, user.userSpecificationType, dropdowns.rdcs]);

  const validateForm = (formData = null, locationMappings = null) => {
    let dataToValidate = formData || user;
    const catsForBv =
      dropdownsRef.current.allBusinessVerticals?.length
        ? dropdownsRef.current.allBusinessVerticals
        : dropdownsRef.current.businessVerticals || [];
    const remappedBv = resolveBusinessVerticalCodes(
      dataToValidate.bvCodes,
      catsForBv,
      dataToValidate.marketCodes || []
    );
    if (remappedBv.length) {
      dataToValidate = { ...dataToValidate, bvCodes: remappedBv };
    }
    
    if (!dataToValidate.userName?.trim()) {
      Swal.fire("Error", "Username is required", "error");
      return false;
    }
    if (!dataToValidate.email?.trim()) {
      Swal.fire("Error", "Email is required", "error");
      return false;
    }
    if (!dataToValidate.fullName?.trim()) {
      Swal.fire("Error", "User Full Name is required", "error");
      return false;
    }
    const spec = normalizeUserSpecificationType(dataToValidate.userSpecificationType);
    if (!spec) {
      Swal.fire("Error", "User Type is required", "error");
      return false;
    }
    if (!dataToValidate.userTypeCode) {
      Swal.fire("Error", "Role is required", "error");
      return false;
    }
    if (!Array.isArray(dataToValidate.marketCodes) || dataToValidate.marketCodes.length === 0) {
      Swal.fire("Error", "Market is required", "error");
      return false;
    }

    if (spec === "Plant") {
      if (!Array.isArray(dataToValidate.plantIds) || dataToValidate.plantIds.length === 0) {
        Swal.fire("Error", "Select at least one Plant", "error");
        return false;
      }
    }
    if (spec === "RDC") {
      if (!Array.isArray(dataToValidate.rdcIds) || dataToValidate.rdcIds.length === 0) {
        Swal.fire("Error", "Select at least one RDC", "error");
        return false;
      }
    }
    if (!Array.isArray(dataToValidate.bvCodes) || dataToValidate.bvCodes.length === 0) {
      Swal.fire("Error", "Business Vertical is required", "error");
      return false;
    }

    const isGeneral = isGeneralUserSpec(dataToValidate);
    const mappings =
      locationMappings != null
        ? locationMappings
        : dataToValidate.locationMappings || [];

    const categoryGeoError = validateUserCategoryAndGeo({
      categoryCodes: dataToValidate.bvCodes,
      locationMappings: mappings,
      allowedCategories:
        dropdownsRef.current.allBusinessVerticals?.length
          ? dropdownsRef.current.allBusinessVerticals
          : dropdownsRef.current.businessVerticals || [],
      geoMasters: {
        countries: dropdownsRef.current.countries || [],
        zones: dropdownsRef.current.zones || [],
        regions: dropdownsRef.current.regions || [],
        territories: dropdownsRef.current.territories || [],
      },
      isGeneralUser: isGeneral,
      // Edit mode: full replace of mappings — exclude nothing from final set,
      // but ignore stale MappingId values when comparing combinations.
      excludeMappingId: null,
    });
    if (categoryGeoError) {
      Swal.fire("Error", categoryGeoError, "error");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!canSave) {
      Swal.fire("Access Denied", "You do not have permission to save.", "error");
      return;
    }
    if (loading || (isEditMode && !String(user?.userName || "").trim())) {
      Swal.fire("Please wait", "User details are still loading. Try again in a moment.", "info");
      return;
    }

    const joinCsv = (arr) =>
      Array.isArray(arr)
        ? arr
            .map((s) => (typeof s === "string" ? s.trim() : s))
            .filter(Boolean)
            .join(",")
        : "";

    const formData = { ...user };
    const catsForBv =
      dropdownsRef.current.allBusinessVerticals?.length
        ? dropdownsRef.current.allBusinessVerticals
        : dropdownsRef.current.businessVerticals || [];
    const remappedBv = resolveBusinessVerticalCodes(
      formData.bvCodes,
      catsForBv,
      formData.marketCodes || []
    );
    if (remappedBv.length) formData.bvCodes = remappedBv;
    const specNorm = normalizeUserSpecificationType(formData.userSpecificationType);
    const isGeneralUser = !isPlantOrRdcSpec(specNorm);
    const allCountryIds = (dropdownsRef.current.countries || [])
      .map((c) => c.countryCode || c.CountryCode)
      .filter((c) => c && String(c).trim());
    const allZoneIds = (dropdownsRef.current.zones || [])
      .map((z) => z.zoneCode || z.ZoneCode)
      .filter((c) => c && String(c).trim());
    const allRegionIds = (dropdownsRef.current.regions || [])
      .map((r) => r.regionCode || r.RegionCode)
      .filter((c) => c && String(c).trim());
    const allTerritoryIds = (dropdownsRef.current.territories || [])
      .map((t) => t.territoryCode || t.TerritoryCode)
      .filter((c) => c && String(c).trim());
    const geoResolved = resolveUserGeoCodesForSave(
      isGeneralUser ? formData : { territoryCodes: [] },
      {
        allCountryIds: isGeneralUser ? allCountryIds : [],
        allZoneIds: isGeneralUser ? allZoneIds : [],
        allRegionIds: isGeneralUser ? allRegionIds : [],
        allTerritoryIds: isGeneralUser ? allTerritoryIds : [],
      }
    );

    const locationMappings = isGeneralUser
      ? buildLocationMappingsFromSelections(
          {
            countryCodes: geoResolved.countryCodes,
            zoneCodes: geoResolved.zoneCodes,
            regionCodes: geoResolved.regionCodes,
            territoryCodes: geoResolved.territoryCodes,
          },
          {
            territories: dropdownsRef.current.territories || [],
            regions: dropdownsRef.current.regions || [],
            zones: dropdownsRef.current.zones || [],
            countryScopeMode: geoResolved.countryScopeMode,
            zoneScopeMode: geoResolved.zoneScopeMode,
            regionScopeMode: geoResolved.regionScopeMode,
            territoryScopeMode: geoResolved.territoryScopeMode,
            existingMappings: formData.locationMappings || [],
          }
        )
      : [];

    const payload = {
      ...formData,
      locationMappings,
      countryCodes: geoResolved.countryCodes,
      zoneCodes: geoResolved.zoneCodes,
      regionCodes: geoResolved.regionCodes,
      territoryCodes: geoResolved.territoryCodes,
      countryScopeMode: geoResolved.countryScopeMode,
      zoneScopeMode: geoResolved.zoneScopeMode,
      regionScopeMode: geoResolved.regionScopeMode,
      territoryScopeMode: geoResolved.territoryScopeMode,
      countryCode: geoResolved.countryCode,
      zoneCode: geoResolved.zoneCode,
      regionCode: geoResolved.regionCode,
      territoryCode: geoResolved.territoryCode,
      // Store CATEGORYMASTER.CATGCODE CSV in UserMaster.BVCode
      catgCode: joinCsv(formData.bvCodes),
      bvCode: joinCsv(formData.bvCodes), // backward compatibility
      marketCodes: formData.marketCodes || [],
      marketCode: joinCsv(formData.marketCodes),
      // Always send General/Plant/RDC/BCM/CPD. Null made sp_UpdateUser keep the old value
      // (ISNULL(@UserSpecificationType, UserSpecificationType)), so User Type never updated to General.
      userSpecificationType: specNorm || "General",
      plantIds: buildUserMasterPlantIdsCsv(
        specNorm,
        formData.plantIds,
        formData.rdcIds,
        joinCsv
      ),
      rdcIds: "",
    };

    if (!validateForm(formData, locationMappings)) {
      setSaving(false);
      return;
    }

    try {
      const usersRes = await userAPI.getAll({ status: "all" });
      const existingUsers = usersRes?.data ?? usersRes?.Data ?? [];
      const catsForScope =
        dropdownsRef.current.allBusinessVerticals?.length
          ? dropdownsRef.current.allBusinessVerticals
          : dropdownsRef.current.businessVerticals || [];
      const roleInfo = resolveUserTypeFromMaster(
        dropdownsRef.current.userTypes || [],
        formData.userTypeCode
      );
      const slots = buildRoleScopeSlots({
        roleKey: formData.userTypeCode,
        roleLabel: roleInfo?.userTypeName || formData.userTypeCode,
        bvKeys: bvKeysFromCodes(formData.bvCodes, catsForScope),
        userSpec: specNorm || "General",
        mappings: locationMappings,
        plantCodes: specNorm === "Plant" ? formData.plantIds || [] : [],
        rdcCodes: specNorm === "RDC" ? formData.rdcIds || [] : [],
        ownerId: isEditMode ? userIdCode : "",
        ownerAd: formData.userName,
        ownerName: formData.fullName,
      });
      const occupied = [];
      const scopeMasters = {
        countries: dropdownsRef.current.countries || [],
        zones: dropdownsRef.current.zones || [],
        regions: dropdownsRef.current.regions || [],
        territories: dropdownsRef.current.territories || [],
        businessVerticals: catsForScope,
      };
      for (const existingUser of Array.isArray(existingUsers) ? existingUsers : []) {
        occupied.push(...slotsFromListedUser(existingUser, scopeMasters));
      }
      const scopeConflict = findRoleScopeConflict({ slots }, occupied);
      if (scopeConflict) {
        Swal.fire("Error", scopeConflict, "error");
        setSaving(false);
        return;
      }
    } catch (scopeErr) {
      console.warn("Role scope check skipped.", scopeErr);
    }
    
    setUser(formData);

    setSaving(true);
    try {
      const createdBy = currentUser?.userIdCode || "SYSADMIN001";
      let response;

      if (isEditMode) {
        response = await userAPI.update(userIdCode, payload, createdBy);
      } else {
        response = await userAPI.create(
          {
            ...payload,
            userIdCode: payload.userIdCode || null, // Send null if empty to trigger auto-generation
          },
          createdBy
        );
      }

      if (response && response.success) {
        const result = await Swal.fire({
          title: "Success",
          text: response.message || "User saved successfully",
          icon: "success",
          confirmButtonText: "OK",
        });
        if (result.isConfirmed) {
          navigate("/users");
        }
      } else {
        Swal.fire("Error", response.message || "Failed to save user", "error");
      }
    } catch (e) {
      console.error("Error saving user:", e);
      Swal.fire(
        "Error",
        e.response?.data?.message || "Failed to save user. Please try again.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const parseBulkExcel = async (file) => {
    if (!file) {
      setBulkRows([]);
      return;
    }
    const ext = (file.name || "").toLowerCase();
    if (!ext.endsWith(".xlsx")) {
      Swal.fire("Error", "Only .xlsx is supported", "error");
      setBulkRows([]);
      return;
    }

    setBulkParsing(true);
    try {
      const buf = await file.arrayBuffer();
      if (!buf || buf.byteLength < 64) {
        Swal.fire("Error", "Excel file is empty or unreadable.", "error");
        setBulkRows([]);
        return;
      }

      // Valid .xlsx is a ZIP (starts with PK)
      const sig = new Uint8Array(buf.slice(0, 2));
      if (sig[0] !== 0x50 || sig[1] !== 0x4b) {
        Swal.fire(
          "Error",
          "This is not a valid .xlsx file (wrong format or corrupted download). Click Download Template (.xlsx) again and upload that file.",
          "error"
        );
        setBulkRows([]);
        return;
      }

      const wb = new ExcelJS.Workbook();
      try {
        await wb.xlsx.load(buf);
      } catch (loadErr) {
        console.error("Excel load error:", loadErr);
        // Retry with a fresh copy of the buffer (some browsers pass detached buffers)
        try {
          const copy = buf.slice(0);
          await wb.xlsx.load(copy);
        } catch (retryErr) {
          console.error("Excel load retry error:", retryErr);
          Swal.fire(
            "Error",
            `Could not open this Excel file (${retryErr?.message || "parse error"}). Use Download Template (.xlsx) from this page and upload that file without re-saving in another format.`,
            "error"
          );
          setBulkRows([]);
          return;
        }
      }

      const picked = await pickUploadWorksheet(wb, findBulkExcelHeaderRow);
      if (!picked) {
        Swal.fire(
          "Error",
          "Invalid Excel. Could not find header row. Use Download Template or /users Excel download (headers: Employee Code, AD ID, User Full Name, Email, ...).",
          "error"
        );
        setBulkRows([]);
        return;
      }
      if (picked.cancelled) {
        setBulkRows([]);
        return;
      }
      const { ws, found } = picked;

      const headerMap = found.headerMap;
      const headerRowNumber = found.rowNumber;

      const missing = USER_BULK_UPLOAD_REQUIRED_HEADERS.filter(
        (h) => headerMap[h] == null
      );
      if (missing.length) {
        Swal.fire(
          "Error",
          `Invalid template. Missing columns: ${missing.join(
            ", "
          )}. Download from /users (Excel) or use Download Template on this page.`,
          "error"
        );
        setBulkRows([]);
        return;
      }

      const get = (row, header) => {
        const col = headerMap[normalizeExcelHeader(header)];
        if (!col) return "";
        return getExcelCellText(row.getCell(col));
      };

      const lastRow = Math.max(ws.rowCount || 0, ws.actualRowCount || 0, headerRowNumber + 1);
      const rows = [];
      for (let r = headerRowNumber + 1; r <= lastRow; r++) {
        const row = ws.getRow(r);
        let any = false;
        try {
          row.eachCell({ includeEmpty: false }, (cell) => {
            if (getExcelCellText(cell)) any = true;
          });
        } catch {
          // fallback: check AD ID / Email columns
          any = !!(get(row, "AD ID") || get(row, "Email"));
        }
        if (!any) continue;

        const adId = get(row, "AD ID");
        // Skip title/summary leftovers without AD ID
        if (!adId) continue;

        const cleanGeo = (v) => {
          const s = String(v || "").trim();
          if (!s || s === "-" || s === "—" || s.toLowerCase() === "n/a") return "";
          return s;
        };

        const specNorm = parseUserSpecificationType(
          get(row, "User Type") || get(row, "User Specification Type")
        ) || "General";
        const plantRdc = cleanGeo(get(row, "Plant / RDC"));
        const isPlant = specNorm === "Plant";
        const isRdc = specNorm === "RDC";

        let country = cleanGeo(get(row, "Country"));
        const zone = cleanGeo(get(row, "Zone"));
        const region = cleanGeo(get(row, "Region"));
        const territory = cleanGeo(get(row, "Territory"));
        // Export-style rows: Zone without Country → Country = ALL
        if (!isPlant && !isRdc && !country && (zone || region || territory)) {
          country = "ALL";
        }

        rows.push({
          rowNumber: r,
          adId,
          employeeCode: get(row, "Employee Code"),
          fullName: get(row, "User Full Name"),
          email: get(row, "Email"),
          mobileNumber: get(row, "Mobile Number"),
          role: get(row, "Role"),
          market: get(row, "Market"),
          country,
          zone,
          region,
          territory,
          businessVertical: cleanGeo(get(row, "Business Vertical")),
          userSpecificationType: specNorm,
          plantIds: isPlant || isRdc ? plantRdc : "",
          rdcIds: isRdc ? plantRdc : "",
          status: get(row, "Status"),
        });
      }

      try {
        const clientErrors = validateBulkUserRows(rows);
        if (clientErrors.length) {
          // Details go to Excel — not the popup body
          const blob = await buildBulkValidationErrorExcel(clientErrors);
          const fileName = `Users_BulkUpload_ValidationErrors_${new Date()
            .toISOString()
            .replace(/[:.]/g, "-")
            .slice(0, 19)}.xlsx`;
          await Swal.fire({
            icon: "warning",
            title: "Validation Failed",
            html: `
              <div style="text-align:left;line-height:1.6">
                <div><strong>${clientErrors.length}</strong> row(s) failed Category/Geo validation.</div>
                <p style="margin:12px 0 0">
                  Download the error Excel for details. On <strong>Submit</strong>, you can continue
                  with the remaining valid rows (insert/update with full server checks).
                </p>
              </div>
            `,
            showDenyButton: true,
            denyButtonText: "Download Failed Excel",
            confirmButtonText: "OK",
            reverseButtons: true,
          }).then((result) => {
            if (result.isDenied) {
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = fileName;
              a.click();
              URL.revokeObjectURL(url);
            }
          });
        }
      } catch (valErr) {
        console.warn("Client bulk validation skipped:", valErr);
      }

      setBulkRows(rows);
      if (!rows.length) {
        Swal.fire(
          "Info",
          "No data rows found in Excel. Ensure headers are present and AD ID is filled.",
          "info"
        );
      }
    } catch (e) {
      console.error("Excel parse error:", e);
      const detail = e?.message ? String(e.message).slice(0, 180) : "";
      Swal.fire(
        "Error",
        detail
          ? `Failed to parse Excel: ${detail}`
          : "Failed to parse Excel. Please re-check the template.",
        "error"
      );
      setBulkRows([]);
    } finally {
      setBulkParsing(false);
    }
  };

  const parseMasterExcel = async (file) => {
    if (!file) {
      setBulkRows([]);
      return;
    }
    const ext = (file.name || "").toLowerCase();
    if (!ext.endsWith(".xlsx")) {
      Swal.fire("Error", "Only .xlsx is supported", "error");
      setBulkRows([]);
      return;
    }

    setBulkParsing(true);
    try {
      const buf = await file.arrayBuffer();
      if (!buf || buf.byteLength < 64) {
        Swal.fire("Error", "Excel file is empty or unreadable.", "error");
        setBulkRows([]);
        return;
      }
      const sig = new Uint8Array(buf.slice(0, 2));
      if (sig[0] !== 0x50 || sig[1] !== 0x4b) {
        Swal.fire(
          "Error",
          "This is not a valid .xlsx file. Use Download Master Template (.xlsx) from this page.",
          "error"
        );
        setBulkRows([]);
        return;
      }

      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(buf);
      const picked = await pickUploadWorksheet(wb, findMasterExcelHeaderRow);
      if (!picked) {
        Swal.fire(
          "Error",
          "Invalid master Excel. No sheet has the required headers (email, FullName, RoleName, Market Type, Country, BusinessVertical).",
          "error"
        );
        setBulkRows([]);
        return;
      }
      if (picked.cancelled) {
        setBulkRows([]);
        return;
      }
      const { ws, found } = picked;

      const headerMap = found.headerMap;
      const headerRowNumber = found.rowNumber;
      const missing = MASTER_BULK_UPLOAD_REQUIRED_HEADERS.filter(
        (h) => headerMap[h] == null
      );
      if (missing.length) {
        Swal.fire(
          "Error",
          `Invalid master template. Missing columns: ${missing.join(", ")}. Use Download Master Template (.xlsx).`,
          "error"
        );
        setBulkRows([]);
        return;
      }

      const getM = (row, canonical) => {
        const col = headerMap[canonical];
        if (!col) return "";
        return getExcelCellText(row.getCell(col));
      };

      const cleanGeo = (v) => {
        const s = String(v || "").trim();
        if (!s || s === "-" || s === "—" || s.toLowerCase() === "n/a") return "";
        return s;
      };

      const lastRow = Math.max(ws.rowCount || 0, ws.actualRowCount || 0, headerRowNumber + 1);
      const rows = [];
      for (let r = headerRowNumber + 1; r <= lastRow; r++) {
        const row = ws.getRow(r);
        const email = getM(row, "EMAIL").trim();
        const fullFromCol = getM(row, "FULLNAME").trim();
        const first = getM(row, "FIRSTNAME").trim();
        const last = getM(row, "LASTNAME").trim();
        const fullName = fullFromCol || `${first} ${last}`.trim();
        if (!email && !fullName) continue;

        const plant = cleanGeo(getM(row, "PLANT"));
        // RoleName must match UserTypeMaster (code or name) — no hardcoded aliases.
        const roleRaw = getM(row, "ROLENAME").trim();
        const role = roleRaw;
        const spec = inferMasterUserType({
          plant,
          role: roleRaw,
          explicit: getM(row, "USER TYPE"),
        });

        let country = cleanGeo(getM(row, "COUNTRY"));
        const zone = cleanGeo(getM(row, "ZONE"));
        const region = cleanGeo(getM(row, "REGION"));
        const territory = cleanGeo(getM(row, "TERRITORY"));
        if (spec !== "Plant" && spec !== "RDC" && !country && (zone || region || territory)) {
          country = "ALL";
        }

        const adId = email.includes("@") ? email.split("@")[0] : email;
        rows.push({
          rowNumber: r,
          adId,
          employeeCode: getM(row, "EMPLOYEE_CODE"),
          fullName,
          email,
          mobileNumber: "",
          role,
          market: getM(row, "MARKET TYPE"),
          country: spec === "Plant" || spec === "RDC" ? "" : country,
          zone: spec === "Plant" || spec === "RDC" ? "" : zone,
          region: spec === "Plant" || spec === "RDC" ? "" : region,
          territory: spec === "Plant" || spec === "RDC" ? "" : territory,
          businessVertical: cleanGeo(getM(row, "BUSINESSVERTICAL")),
          userSpecificationType: spec,
          plantIds: spec === "Plant" ? plant : "",
          rdcIds: spec === "RDC" ? plant : "",
          status: getM(row, "ACTIVE/INACTIVE") || "Active",
        });
      }

      try {
        const valOpts = await getBulkValidationOptions(true);
        // Canonicalize RoleName from live UserTypeMaster (no hardcoded role map).
        const userTypes = valOpts?.masters?.userTypes || [];
        for (const row of rows) {
          const resolved = resolveUserTypeFromMaster(userTypes, row.role);
          if (resolved?.userTypeName) {
            row.role = resolved.userTypeName;
          }
        }
        const clientErrors = validateBulkUserRows(rows, valOpts);
        if (clientErrors.length) {
          const blob = await buildBulkValidationErrorExcel(clientErrors);
          const fileName = `MasterUser_BulkUpload_ValidationErrors_${new Date()
            .toISOString()
            .replace(/[:.]/g, "-")
            .slice(0, 19)}.xlsx`;
          await Swal.fire({
            icon: "warning",
            title: "Validation Failed",
            html: `
              <div style="text-align:left;line-height:1.6">
                <div><strong>${clientErrors.length}</strong> row(s) failed Business Vertical / geo validation.</div>
                <p style="margin:12px 0 0">
                  Download the error Excel for details. On <strong>Submit</strong>, valid rows can still be uploaded.
                </p>
              </div>
            `,
            showDenyButton: true,
            denyButtonText: "Download Failed Excel",
            confirmButtonText: "OK",
            reverseButtons: true,
          }).then((result) => {
            if (result.isDenied) {
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = fileName;
              a.click();
              URL.revokeObjectURL(url);
            }
          });
        }
      } catch (valErr) {
        console.warn("Master bulk validation skipped:", valErr);
      }

      setBulkRows(rows);
      if (!rows.length) {
        Swal.fire("Info", "No data rows found. Ensure email and FullName are filled.", "info");
      }
    } catch (e) {
      console.error("Master Excel parse error:", e);
      Swal.fire(
        "Error",
        e?.message ? `Failed to parse Excel: ${String(e.message).slice(0, 180)}` : "Failed to parse Excel.",
        "error"
      );
      setBulkRows([]);
    } finally {
      setBulkParsing(false);
    }
  };

  const handleDownloadBulkTemplate = async () => {
    try {
      Swal.fire({
        title: "Preparing template…",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const sampleRows = buildBulkUploadSampleRows(dropdownsRef.current || dropdowns);

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("User Management");

      worksheet.mergeCells(1, 1, 1, USER_MANAGEMENT_EXCEL_HEADERS.length);
      const titleRow = worksheet.getRow(1);
      titleRow.getCell(1).value = "User Management Data";
      titleRow.getCell(1).font = { size: 16, bold: true };
      titleRow.getCell(1).alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      titleRow.height = 25;

      const headerRow = worksheet.getRow(3);
      USER_MANAGEMENT_EXCEL_HEADERS.forEach((h, i) => {
        const cell = headerRow.getCell(i + 1);
        cell.value = h;
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF667EEA" },
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });
      headerRow.height = 20;

      sampleRows.forEach((row, index) => {
        const excelRow = worksheet.getRow(4 + index);
        const vals = [
          index + 1,
          row.employeeCode,
          row.adId,
          row.fullName,
          row.email,
          row.mobileNumber,
          row.role,
          row.market,
          row.country,
          row.zone,
          row.region,
          row.territory,
          row.businessVertical,
          row.userType,
          row.plantRdc,
          "",
          "Active",
        ];
        vals.forEach((v, i) => {
          excelRow.getCell(i + 1).value = v;
        });
        excelRow.alignment = { vertical: "middle" };
      });

      worksheet.columns.forEach((col) => {
        col.width = 18;
      });

      const info = workbook.addWorksheet("Instructions");
      info.getCell("A1").value =
        "User Management Bulk Upload — 5 sample rows (General, Plant, RDC, CPD, BCM)";
      info.getCell("A1").font = { bold: true };
      info.getCell("A3").value =
        "1. Replace these 5 sample rows with real users before upload. Do not submit the sample AD IDs as-is.";
      info.getCell("A4").value =
        "2. Required: AD ID, User Full Name, Email, Role, Market, Business Vertical. Country required for General / CPD / BCM users.";
      info.getCell("A5").value =
        "3. User Type must be General, Plant, RDC, CPD, or BCM. Plant / RDC column holds plant or RDC names when User Type is Plant or RDC.";
      info.getCell("A6").value =
        "4. For General / CPD / BCM users, Country is required (or ALL). Plant/RDC rows skip geo (use '-').";
      info.getCell("A7").value =
        "5. Existing AD ID updates the user; new AD ID inserts.";
      info.columns.forEach((col) => {
        col.width = 90;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const xlsxBlob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const fileName = `User_Management_BulkUpload_Sample_${new Date().toISOString().split("T")[0]}.xlsx`;
      const url = URL.createObjectURL(xlsxBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      Swal.close();
      Swal.fire({
        icon: "success",
        title: "Template ready",
        text: `${fileName} downloaded (5 sample rows: General, Plant, RDC, CPD, BCM).`,
        timer: 2200,
        showConfirmButton: false,
      });
    } catch (e) {
      console.error("Template download error:", e);
      Swal.fire(
        "Error",
        e.response?.data?.message || e.message || "Failed to download template (.xlsx).",
        "error"
      );
    }
  };

  const handleDownloadMasterTemplate = async () => {
    try {
      Swal.fire({
        title: "Preparing master template…",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const sampleRows = buildMasterUploadSampleRows();
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("MikasaPLY");

      const headerRow = worksheet.getRow(1);
      MASTER_USER_EXCEL_HEADERS.forEach((h, i) => {
        const cell = headerRow.getCell(i + 1);
        cell.value = h;
        cell.font = { bold: true, size: 11 };
        cell.alignment = { vertical: "middle" };
      });

      sampleRows.forEach((row, index) => {
        const excelRow = worksheet.getRow(2 + index);
        const vals = [
          row.employeeCode,
          row.fullName,
          row.roleName,
          row.userType,
          row.email,
          row.market,
          row.country,
          row.zone,
          row.region,
          row.territory,
          row.plant,
          row.businessVertical,
          row.status,
        ];
        vals.forEach((v, i) => {
          excelRow.getCell(i + 1).value = v;
        });
      });

      const widths = [15, 28, 17, 12, 42, 12, 8, 8, 15, 27, 12, 28, 12];
      worksheet.columns.forEach((col, i) => {
        col.width = widths[i] || 18;
      });

      const info = workbook.addWorksheet("Instructions");
      info.getCell("A1").value =
        "Master Data Upload — MikasaPLY sheet (5 sample rows: General, Plant, RDC, CPD, BCM)";
      info.getCell("A1").font = { bold: true };
      info.getCell("A3").value =
        "1. Use the MikasaPLY sheet. Columns: Employee_code, FullName, RoleName, User Type, email, Market Type, Country, Zone, Region, Territory, Plant, BusinessVertical, Active.";
      info.getCell("A4").value =
        "2. Replace the 5 sample rows with real users before upload. AD ID is taken from the email local-part.";
      info.getCell("A5").value =
        "3. Required: email, FullName, RoleName, Market Type, BusinessVertical. Country is required for General / CPD / BCM users (or ALL).";
      info.getCell("A6").value =
        "4. User Type must be General, Plant, RDC, CPD, or BCM. Plant column holds plant name (User Type Plant) or RDC name (User Type RDC).";
      info.getCell("A7").value =
        "5. Same email on multiple geo rows is allowed (one row per Country / Zone / Region / Territory).";
      info.getCell("A8").value =
        "6. ALL is allowed for Zone / Region / Territory. Existing email / AD ID updates the user; new email inserts.";
      info.getCell("A9").value =
        "7. Use this file only for Master Data Upload — not for Single Upload.";
      info.columns.forEach((col) => {
        col.width = 110;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const xlsxBlob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const fileName = "MikasaPLY users_sample.xlsx";
      const url = URL.createObjectURL(xlsxBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      Swal.close();
      Swal.fire({
        icon: "success",
        title: "Template ready",
        text: `${fileName} downloaded (MikasaPLY sheet includes User Type: General, Plant, RDC, CPD, or BCM).`,
        timer: 2600,
        showConfirmButton: false,
      });
    } catch (e) {
      console.error("Master template download error:", e);
      Swal.fire(
        "Error",
        e.response?.data?.message || e.message || "Failed to download master template (.xlsx).",
        "error"
      );
    }
  };

  const triggerBlobDownload = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  const downloadBulkReportFile = async ({
    reportUrl,
    mode,
    resultRows,
    filePrefix,
  }) => {
    const stamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .slice(0, 19);
    const fileName = `${filePrefix}_${stamp}.xlsx`;

    // Prefer authenticated fetch — /uploads requires a session; window.open won't send it.
    if (reportUrl) {
      try {
        const path = reportUrl.startsWith("http")
          ? reportUrl
          : reportUrl.startsWith("/")
            ? reportUrl
            : `/${reportUrl}`;
        const resp = await api.get(path, {
          responseType: "blob",
          timeout: API_UPLOAD_TIMEOUT_MS,
        });
        const blob = resp?.data;
        if (blob && (blob.size == null || blob.size > 0)) {
          // Guard: API may return JSON error as blob
          if (blob.type && blob.type.includes("application/json")) {
            throw new Error("Auth/report error from server");
          }
          triggerBlobDownload(blob, fileName);
          return;
        }
      } catch (err) {
        console.warn("Authenticated bulk report download failed, using client Excel:", err);
      }
    }

    // Fallback: build Excel from API row results in the response
    const byRow = new Map(
      (Array.isArray(bulkRows) ? bulkRows : []).map((r) => [
        Number(r.rowNumber),
        r,
      ])
    );
    const enriched = (resultRows || []).map((r) => {
      const rn = Number(r.rowNumber ?? r.RowNumber);
      const src = byRow.get(rn);
      const emp =
        r.employeeCode ??
        r.EmployeeCode ??
        src?.employeeCode ??
        "";
      return { ...r, employeeCode: emp, EmployeeCode: emp };
    });
    const blob = await buildBulkUploadResultExcel(enriched, mode);
    triggerBlobDownload(blob, fileName);
  };

  const showBulkUploadResult = async (res) => {
      const data = res?.data;
      const total = data?.totalRows ?? data?.TotalRows ?? 0;
      const inserted =
        data?.insertedCount ?? data?.InsertedCount ?? 0;
      const updated = data?.updatedCount ?? data?.UpdatedCount ?? 0;
      const ok =
        data?.successCount ??
        data?.SuccessCount ??
        inserted + updated;
      const fail = data?.failedCount ?? data?.FailedCount ?? 0;
      const reportUrl = data?.errorReportUrl || data?.ErrorReportUrl || "";
      const successReportUrl =
        data?.successReportUrl || data?.SuccessReportUrl || "";
      const resultRows = data?.rows || data?.Rows || [];
      const hasSuccessDownload = ok > 0 || !!successReportUrl;
      const hasFailedDownload = fail > 0 || !!reportUrl;

      const summaryHtml = `
        <div class="bulk-upload-swal-stats">
          <div class="bulk-upload-swal-stat">
            <span class="bulk-upload-swal-stat-value">${total}</span>
            <span class="bulk-upload-swal-stat-label">Total</span>
          </div>
          <div class="bulk-upload-swal-stat bulk-upload-swal-stat-insert">
            <span class="bulk-upload-swal-stat-value">${inserted}</span>
            <span class="bulk-upload-swal-stat-label">Inserted</span>
          </div>
          <div class="bulk-upload-swal-stat bulk-upload-swal-stat-update">
            <span class="bulk-upload-swal-stat-value">${updated}</span>
            <span class="bulk-upload-swal-stat-label">Updated</span>
          </div>
          <div class="bulk-upload-swal-stat bulk-upload-swal-stat-ok">
            <span class="bulk-upload-swal-stat-value">${ok}</span>
            <span class="bulk-upload-swal-stat-label">Success</span>
          </div>
          <div class="bulk-upload-swal-stat ${fail > 0 ? "bulk-upload-swal-stat-fail" : ""}">
            <span class="bulk-upload-swal-stat-value">${fail}</span>
            <span class="bulk-upload-swal-stat-label">Failed</span>
          </div>
        </div>
      `;

      const result = await showSwal({
        title: res?.success ? "Master Data Upload Result" : "Master Data Upload",
        message: summaryHtml,
        allowHtml: true,
        icon: res?.success ? (fail > 0 ? "warning" : "success") : "error",
        confirmText: "OK",
        showCloseButton: true,
        showDenyButton: hasSuccessDownload,
        denyButtonText: "Download Success Excel",
        showCancelButton: hasFailedDownload,
        cancelButtonText: "Download Failed Excel",
        reverseButtons: true,
        allowOutsideClick: false,
        width: 440,
        customClass: {
          popup: "modern-swal-popup bulk-upload-swal-popup",
          denyButton: "modern-swal-deny",
          cancelButton: "modern-swal-cancel",
        },
      });

      if (result.isDenied) {
        await downloadBulkReportFile({
          reportUrl: successReportUrl,
          mode: "success",
          resultRows,
          filePrefix: "Users_BulkUpload_Success",
        }).catch((err) => {
          console.error(err);
          Swal.fire("Error", "Could not download Success Excel.", "error");
        });
        return false;
      }
      if (result.dismiss === Swal.DismissReason.cancel) {
        await downloadBulkReportFile({
          reportUrl,
          mode: "failed",
          resultRows,
          filePrefix: "Users_BulkUpload_Errors",
        }).catch((err) => {
          console.error(err);
          Swal.fire("Error", "Could not download Failed Excel.", "error");
        });
        return false;
      }
      if (result.isConfirmed && res?.success) {
        navigate("/users");
        return true;
      }
      return false;
  };

  const submitBulkRowsToApi = async (rowsToSubmit) => {
      setBulkUploading(true);
      try {
        const createdBy = currentUser?.userIdCode || "SYSADMIN001";
        const res = await userAPI.bulkSubmit(rowsToSubmit, createdBy);
        const wentToList = await showBulkUploadResult(res);
        if (wentToList) return;
        if (bulkMastersRef.current) {
          bulkMastersRef.current.existingUsersLoaded = false;
          bulkMastersRef.current.existingUsers = [];
        }
        await loadDropdowns();
      } finally {
        setBulkUploading(false);
      }
  };

  const handleBulkUpload = async () => {
    try {
      if (uploadMode !== "master") {
        Swal.fire("Error", "Use Master Data Upload to submit Excel files.", "error");
        return;
      }
      if (!Array.isArray(bulkRows) || bulkRows.length === 0) {
        Swal.fire("Error", "No rows to submit. Please select an Excel file first.", "error");
        return;
      }

      const isMaster = true;
      const valOpts = await getBulkValidationOptions(true);
      const userTypes = valOpts?.masters?.userTypes || [];
      // Resolve RoleName from live UserTypeMaster only (no hardcoded aliases).
      for (const row of bulkRows) {
        const resolved = resolveUserTypeFromMaster(userTypes, row.role);
        if (resolved?.userTypeName) {
          row.role = resolved.userTypeName;
        }
      }
      const clientErrors = validateBulkUserRows(bulkRows, valOpts);
      let rowsToSubmit = bulkRows;

      if (clientErrors.length) {
        const failedRowNums = new Set(
          clientErrors.map((e) => Number(e.rowNumber)).filter((n) => !Number.isNaN(n))
        );
        const validRows = bulkRows.filter(
          (r) => !failedRowNums.has(Number(r.rowNumber))
        );
        const blob = await buildBulkValidationErrorExcel(clientErrors);
        const fileName = `Users_BulkUpload_ValidationErrors_${new Date()
          .toISOString()
          .replace(/[:.]/g, "-")
          .slice(0, 19)}.xlsx`;

        const result = await Swal.fire({
          icon: "warning",
          title: "Validation Failed",
          html: `
            <div style="text-align:left;line-height:1.6">
              <div><strong>${clientErrors.length}</strong> row(s) failed client validation.</div>
              <div><strong>${validRows.length}</strong> valid row(s) can still be inserted/updated.</div>
              <p style="margin:12px 0 0">
                Download the error Excel for failed rows, then click <strong>OK</strong> to continue
                with valid rows (server validation still applies).
              </p>
            </div>
          `,
          showDenyButton: true,
          denyButtonText: "Download Failed Excel",
          confirmButtonText: validRows.length ? "OK — Continue" : "OK",
          reverseButtons: true,
          allowOutsideClick: false,
        });

        if (result.isDenied) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = fileName;
          a.click();
          URL.revokeObjectURL(url);
          // Ask again whether to continue after download
          if (validRows.length > 0) {
            const cont = await Swal.fire({
              icon: "question",
              title: "Continue upload?",
              html: `Upload <strong>${validRows.length}</strong> valid row(s) now?<br/><small>Failed rows were skipped.</small>`,
              showCancelButton: true,
              confirmButtonText: "Yes, continue",
              cancelButtonText: "Cancel",
            });
            if (!cont.isConfirmed) return;
            rowsToSubmit = validRows;
          } else {
            return;
          }
        } else if (result.isConfirmed) {
          // OK → proceed with valid rows only
          if (validRows.length === 0) {
            Swal.fire(
              "Info",
              "No valid rows to upload. Fix failed rows using the error Excel and try again.",
              "info"
            );
            return;
          }
          rowsToSubmit = validRows;
        } else {
          return;
        }
      }

      if (isMaster) {
        rowsToSubmit = collapseMasterBulkRows(rowsToSubmit);
        if (!rowsToSubmit.length) {
          Swal.fire(
            "Info",
            "No valid users to upload after grouping by email.",
            "info"
          );
          return;
        }
      }

      await submitBulkRowsToApi(rowsToSubmit);
    } catch (e) {
      console.error("Bulk upload error:", e);
      Swal.fire(
        "Error",
        e.response?.data?.message || "Bulk upload failed. Please try again.",
        "error"
      );
      setBulkUploading(false);
    }
  };

  if (loading) {
    return (
      <Box className="add-edit-user-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box className="add-edit-user-header">
        <Breadcrumbs
          aria-label="breadcrumb"
          className="add-edit-user-breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href="/users"
            onClick={(e) => {
              e.preventDefault();
              navigate("/users");
            }}
            className="breadcrumb-link brdcrmbLstitms"
          >
            User Management
          </Link>
          <Typography
            color="text.primary"
            className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
          >
            {isEditMode ? "Edit User" : "Add User"}
          </Typography>
        </Breadcrumbs>
        <Box className="add-edit-user-header-right">
          <Button
            className="add-edit-user-back-button"
            onClick={() => navigate("/users")}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          className="add-edit-user-error-alert"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <div className="container-fluid add-edit-user-page">
        {!isEditMode && (
          <div className="card-panel mb-3 bulk-upload-card">
            <div className="bulk-upload-card-header">
              <div className="bulk-upload-card-title">User Upload</div>
              <div className="bulk-upload-card-subtitle">
                Choose single user add, or Master Data Upload (Excel).
              </div>
            </div>

            <div className="row g-3 align-items-end bulk-upload-card-body">
              <div className="col-12">
                <div className="bulk-upload-mode-toggle">
                  <Button
                    variant={uploadMode === "single" ? "contained" : "outlined"}
                    onClick={() => switchUploadMode("single")}
                    className={`funbtns bulk-mode-btn ${uploadMode === "single" ? "bulk-mode-btn-active" : ""}`}
                    disabled={!canSave || bulkUploading || bulkParsing}
                  >
                    Single Upload
                  </Button>
                  <Button
                    variant={uploadMode === "master" ? "contained" : "outlined"}
                    onClick={() => switchUploadMode("master")}
                    className={`funbtns bulk-mode-btn ${uploadMode === "master" ? "bulk-mode-btn-active" : ""}`}
                    disabled={!canSave || bulkUploading || bulkParsing}
                  >
                    Master Data Upload
                  </Button>
                </div>
              </div>

              {uploadMode === "master" && (
                <>
                  <div className="col-12">
                    <FileUploadPanelLayout
                      title="Master Data Upload (Excel)"
                      helperText=""
                      accept=".xlsx"
                      multiple={false}
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        setBulkFile(f);
                        parseMasterExcel(f);
                        e.target.value = "";
                      }}
                      disabled={bulkUploading || bulkParsing || !canSave}
                      chooseStyle={{
                        opacity: bulkUploading || bulkParsing || !canSave ? 0.6 : 1,
                      }}
                      footerContent={
                        <div className="bulk-upload-template-row">
                          <Button
                            type="button"
                            onClick={handleDownloadMasterTemplate}
                            variant="contained"
                            className="funbtns bulk-upload-template-btn"
                            startIcon={<FileDownloadOutlinedIcon />}
                            title="Download MikasaPLY sample template (.xlsx)"
                            disabled={bulkUploading || bulkParsing}
                          >
                            Download Master Template (.xlsx)
                          </Button>
                        </div>
                      }
                    />
                  </div>
                </>
              )}
            </div>

            {uploadMode === "master" && (
              <div className="row mt-3">
                <div className="col-12">
                  <div className="bulk-preview-meta">
                    <span className="bulk-preview-label">Preview Rows</span>
                    <span className="bulk-preview-count">{bulkRows.length}</span>
                    {bulkParsing && <span className="bulk-preview-status">Parsing...</span>}
                  </div>
                  <div className="table-responsive bulk-preview-grid">
                    <table
                      ref={bulkTableRef}
                      className="table table-striped table-bordered complaint-table custmTblLst"
                      style={{ width: "100%" }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Basic Information Section */}
        {(!isEditMode && uploadMode === "master") ? null : (
        <div className="card-panel mb-3">
          {/* <div className="section-heading mb-3">
            <Typography variant="h6" className="add-edit-user-section-heading">
              Basic Information
            </Typography>
          </div> */}
          <div className="row g-3 mb-4">
            {isEditMode && (
            <div className="col-12 col-md-6 col-lg-3">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  User ID Code <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={user.userIdCode}
                  required
                  fullWidth
                  disabled={true}
                  size="small"
                  className="add-edit-user-textfield"
                />
              </div>
            </div>
            )}






            <div className="col-12 col-md-6 col-lg-3">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  AD ID <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={user.userName}
                  onChange={(e) => handleChange("userName", e.target.value)}
                  required
                  fullWidth
                  disabled={isEditMode}
                  size="small"
                  placeholder="Enter AD ID"
                  className="add-edit-user-textfield"
                />
              </div>
            </div>














            <div className="col-12 col-md-6 col-lg-3">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">Employee Code</label>
                <TextField
                  value={user.employeeCode}
                  onChange={(e) => handleChange("employeeCode", e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Enter Employee Code"
                  className="add-edit-user-textfield"
                />
              </div>
            </div>


            <div className="col-12 col-md-6 col-lg-3">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  User Full Name <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={user.fullName}
                  onChange={(e) => handleChange("fullName", e.target.value)}
                  required
                  fullWidth
                  size="small"
                  placeholder="Enter user full name"
                  className="add-edit-user-textfield"
                />
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                Employee email ID <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  type="email"
                  value={user.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  required
                  fullWidth
                  size="small"
                  placeholder="Enter Employee email ID"
                  className="add-edit-user-textfield"
                />
              </div>
            </div>


            <div className="col-12 col-md-6 col-lg-3">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">Mobile Number</label>
                <TextField
                  value={user.phoneNumber}
                  onChange={(e) => handleChange("phoneNumber", e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Enter Mobile Number"
                  className="add-edit-user-textfield"
                />
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <div className="select2-wrapper">
                <label className="select2-label pnlLblsze">
                  User Type <span style={{ color: "red" }}>*</span>
                </label>
                <CustomMultiSelect
                  options={[
                    { id: "General", text: "General" },
                    { id: "Plant", text: "Plant" },
                    { id: "RDC", text: "RDC" },
                    { id: "BCM", text: "BCM" },
                    { id: "CPD", text: "CPD" },
                  ]}
                  value={[normalizeUserSpecificationType(user.userSpecificationType)]}
                  onChange={(selected) => {
                    const last =
                      Array.isArray(selected) && selected.length
                        ? selected[selected.length - 1]
                        : "";
                    handleChange("userSpecificationType", last || "General");
                  }}
                  placeholder="Select User Type"
                  disabled={false}
                  showSelectAll={false}
                  singleSelect
                />
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <div className="select2-wrapper">
                <label className="select2-label pnlLblsze">
                  Role <span style={{ color: "red" }}>*</span>
                </label>
                <CustomMultiSelect
                  options={(dropdowns.userTypes || []).map((ut) => ({
                    id: ut.userTypeCode || ut.UserTypeCode || "",
                    text: ut.userTypeName || ut.UserTypeName || ut.userTypeCode || ut.UserTypeCode || "",
                  }))}
                  value={user.userTypeCode ? [user.userTypeCode] : []}
                  onChange={(selected) => {
                    const last = Array.isArray(selected) && selected.length ? selected[selected.length - 1] : "";
                    handleChange("userTypeCode", last || "");
                  }}
                  placeholder="Select Role"
                  disabled={false}
                  showSelectAll={false}
                  singleSelect
                />
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <div className="select2-wrapper">
                <label className="select2-label pnlLblsze">
                  Market <span style={{ color: "red" }}>*</span>
                </label>
                <CustomMultiSelect
                  options={(dropdowns.markets || []).map((m) => ({
                    id: m.marketCode || m.MARKETCODE || m.MarketCode || "",
                    text:
                      m.marketName ||
                      m.MARKETNAME ||
                      m.MarketName ||
                      m.marketCode ||
                      m.MARKETCODE ||
                      m.MarketCode ||
                      "",
                  }))}
                  value={user.marketCodes || []}
                  onChange={(selected) =>
                    handleChange("marketCodes", Array.isArray(selected) ? selected : [])
                  }
                  placeholder="Select market(s)"
                  disabled={false}
                  showSelectAll
                />
              </div>
            </div>

            {user.userSpecificationType === "Plant" && (
              <div className="col-12 col-md-6 col-lg-3">
                <div className="select2-wrapper">
                  <label className="select2-label pnlLblsze">
                    Plant(s) <span style={{ color: "red" }}>*</span>
                  </label>
                  <CustomMultiSelect
                    options={(dropdowns.plants || [])
                      .map((p) => {
                        const id = String(p.plantCode ?? p.PlantCode ?? "").trim();
                        const name = String(p.plantName ?? p.PlantName ?? "").trim();
                        const text = name || id;
                        return { id, text };
                      })
                      .filter((o) => o.id)}
                    value={user.plantIds || []}
                    onChange={(selected) =>
                      handleChange("plantIds", Array.isArray(selected) ? selected : [])
                    }
                    placeholder="Select plant(s) from Plant Master"
                    disabled={false}
                    showSelectAll
                  />
                </div>
              </div>
            )}

            {user.userSpecificationType === "RDC" && (
              <div className="col-12 col-md-6 col-lg-3">
                <div className="select2-wrapper">
                  <label className="select2-label pnlLblsze">
                    RDC(s) <span style={{ color: "red" }}>*</span>
                  </label>
                  <CustomMultiSelect
                    options={(dropdowns.rdcs || [])
                      .map((r) => {
                        const id = String(r.rdcCode ?? r.RdcCode ?? r.RDCCODE ?? "").trim();
                        const name = String(r.rdcName ?? r.RdcName ?? r.RDCNAME ?? "").trim();
                        const text = name || id;
                        return { id, text };
                      })
                      .filter((o) => o.id)}
                    value={user.rdcIds || []}
                    onChange={(selected) =>
                      handleChange("rdcIds", Array.isArray(selected) ? selected : [])
                    }
                    placeholder="Select RDC(s) from RDC Master"
                    disabled={false}
                    showSelectAll
                  />
                </div>
              </div>
            )}

            {(!isPlantOrRdcSpec(user.userSpecificationType)) && (
              <>
                {(isExportCascade
                  ? ["zone", "country", "region", "territory"]
                  : ["country", "zone", "region", "territory"]
                ).map((geoKey) => {
                  if (geoKey === "country") {
                    const countryList = isExportCascade
                      ? exportCountryOptions
                      : dropdowns.countries || [];
                    return (
                      <div key="country" className="col-12 col-md-6 col-lg-3">
                        <div className="select2-wrapper">
                          <label className="select2-label pnlLblsze">Country</label>
                          <CustomMultiSelect
                            options={filterOmittedGeoMasterOptions(
                              countryList.map((c) => ({
                                id: c.countryCode || c.CountryCode || "",
                                text:
                                  c.countryName ||
                                  c.CountryName ||
                                  c.countryCode ||
                                  c.CountryCode ||
                                  "",
                              }))
                            )}
                            value={filterOmittedGeoCodes(user.countryCodes)}
                            onChange={(selected) =>
                              handleChange("countryCodes", filterOmittedGeoCodes(selected))
                            }
                            placeholder={
                              isExportCascade
                                ? user.zoneCodes?.length
                                  ? "Select country(ies)"
                                  : "Select zone first"
                                : "Select Country"
                            }
                            disabled={isExportCascade && !user.zoneCodes?.length}
                          />
                        </div>
                      </div>
                    );
                  }
                  if (geoKey === "zone") {
                    return (
                      <div key="zone" className="col-12 col-md-6 col-lg-3">
                        <div className="select2-wrapper">
                          <label className="select2-label pnlLblsze">Zone</label>
                          <CustomMultiSelect
                            options={filterOmittedGeoMasterOptions(
                              (dropdowns.zones || []).map((z) => ({
                                id: z.zoneCode || z.ZoneCode || "",
                                text: formatZoneOptionLabel(z, dropdowns.countries, {
                                  includeCountry: !isExportCascade,
                                }),
                              }))
                            )}
                            value={filterOmittedGeoCodes(
                              (user.zoneCodes || []).map((code) => {
                                const sel = String(code ?? "").trim();
                                const z = (dropdowns.zones || []).find((x) => {
                                  const id = String(x.zoneCode || x.ZoneCode || "").trim();
                                  if (id === sel) return true;
                                  return (
                                    Array.isArray(x.zoneCodes) &&
                                    x.zoneCodes.some((c) => String(c).trim() === sel)
                                  );
                                });
                                return z?.zoneCode || z?.ZoneCode || code;
                              })
                            )}
                            onChange={(selected) =>
                              handleChange("zoneCodes", filterOmittedGeoCodes(selected))
                            }
                            placeholder={
                              isExportCascade
                                ? "Select zone(s)"
                                : user.countryCodes?.length
                                  ? "Select zone(s)"
                                  : "Select country first"
                            }
                            disabled={!isExportCascade && !user.countryCodes?.length}
                          />
                        </div>
                      </div>
                    );
                  }
                  if (geoKey === "region") {
                    const regionReady = isExportCascade
                      ? user.zoneCodes?.length && user.countryCodes?.length
                      : user.zoneCodes?.length;
                    return (
                      <div key="region" className="col-12 col-md-6 col-lg-3">
                        <div className="select2-wrapper">
                          <label className="select2-label pnlLblsze">Region</label>
                          <CustomMultiSelect
                            options={filterOmittedGeoMasterOptions(
                              (dropdowns.regions || []).map((r) => ({
                                id: r.regionCode || r.RegionCode || "",
                                text:
                                  r.regionName ||
                                  r.RegionName ||
                                  r.regionCode ||
                                  r.RegionCode ||
                                  "",
                              }))
                            )}
                            value={filterOmittedGeoCodes(user.regionCodes)}
                            onChange={(selected) =>
                              handleChange("regionCodes", filterOmittedGeoCodes(selected))
                            }
                            placeholder={
                              regionReady
                                ? "Select region(s)"
                                : isExportCascade
                                  ? "Select country first"
                                  : "Select zone first"
                            }
                            disabled={!regionReady}
                          />
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key="territory" className="col-12 col-md-6 col-lg-3">
                      <div className="select2-wrapper">
                        <label className="select2-label pnlLblsze">Territory</label>
                        <CustomMultiSelect
                          options={filterOmittedGeoMasterOptions(
                            (dropdowns.territories || []).map((t) => ({
                              id: t.territoryCode || t.TerritoryCode || "",
                              text:
                                t.territoryName ||
                                t.TerritoryName ||
                                t.territoryCode ||
                                t.TerritoryCode ||
                                "",
                            }))
                          )}
                          value={filterOmittedGeoCodes(user.territoryCodes)}
                          onChange={(selected) =>
                            handleChange("territoryCodes", filterOmittedGeoCodes(selected))
                          }
                          placeholder={
                            user.regionCodes?.length
                              ? "Select territory(ies)"
                              : "Select region first"
                          }
                          disabled={!user.regionCodes?.length}
                          selectAllLabel={
                            (dropdowns.territories || []).length > 0
                              ? `Select all territories (${(dropdowns.territories || []).length})`
                              : "Select all territories"
                          }
                        />
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            <div className="col-12 col-md-6 col-lg-3">
              <div className="select2-wrapper">
                <label className="select2-label pnlLblsze">
                Business Vertical <span style={{ color: "red" }}>*</span>
                </label>
                <CustomMultiSelect
                  options={(dropdowns.businessVerticals || [])
                    .filter((c) => {
                      const mkt = String(
                        c.marketCode || c.MARKETCODE || c.MarketCode || ""
                      ).trim();
                      const codes = (user.marketCodes || []).map((x) =>
                        String(x || "").trim().toUpperCase()
                      );
                      return (
                        !codes.length ||
                        !mkt ||
                        codes.includes(mkt.toUpperCase())
                      );
                    })
                    .map((c) => ({
                      id: c.catgCode || c.CATGCODE || c.CatgCode || "",
                      text:
                        c.catgName ||
                        c.CATGNAME ||
                        c.CatgName ||
                        c.catgId ||
                        c.CATGID ||
                        c.CatgId ||
                        c.catgCode ||
                        c.CATGCODE ||
                        "",
                    }))}
                  value={user.bvCodes || []}
                  onChange={(selected) => handleChange("bvCodes", selected)}
                  placeholder="Select Business Vertical"
                  disabled={false}
                />
              </div>
            </div>

          </div>

        </div>
        )}

        {/* User Type & Hierarchy Section */}


        {/* Location & Organization Section */}


        {/* Integration Codes Section */}
 

        {/* Action Buttons — same style for Single and Bulk */}
        {(isEditMode || uploadMode === "single" || uploadMode === "master") && (
          <div className="row mt-4">
            <div className="col-12">
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="contained"
                  onClick={() => navigate("/users")}
                  className="btn-cancel-user funbtns"
                  startIcon={<CancelIcon />}
                  disabled={bulkUploading || saving}
                >
                  Cancel
                </Button>
                {uploadMode === "master" && !isEditMode ? (
                  <Button
                    variant="contained"
                    onClick={handleBulkUpload}
                    disabled={bulkUploading || bulkParsing || !canSave}
                    startIcon={
                      bulkUploading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    className="btn-save-user funbtns"
                  >
                    {bulkUploading ? "Submitting..." : "Submit"}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || loading || !canSave || (isEditMode && !String(user?.userName || "").trim())}
                    startIcon={
                      saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />
                    }
                    className="btn-save-user funbtns"
                  >
                    {saving ? "Saving..." : "Save"}
                  </Button>
                )}
              </Stack>
            </div>
          </div>
        )}
      </div>
    </Box>
  );
};

export default AddEditUser;
